/* ballstories Konfigurator — Engine (generischer, schema-getriebener Renderer)
 *
 * Liest das Schema aus window.BSConfigurator (bs-configurator-schema.js) und
 * rendert den Wizard. Portiert die Vue-Logik (Create.vue):
 *   - State + dot-path get/set
 *   - Renderer pro Feldtyp (chips, text, date, city, list, preview, checkbox)
 *   - Navigation + Pro-Step-Validierung (required, custom validate, showIf, list-min)
 *   - KI-Preview via Firebase `generatePreviewImage` (Mapping wie geminiService.js)
 *   - Cart-Add via /cart/add.js mit Line Item Properties (inkl. _bookPayload) → Checkout
 *
 * Kein Pre-Cart-Write nach Firebase: das Buch wird post-Order vom orders/paid-
 * Webhook in Firestore angelegt (Quelle = _bookPayload Line-Item-Property).
 *
 * Kundendaten (E-Mail, Adresse) sammelt der Shopify-Checkout; der orders/paid
 * Webhook backfillt sie später in Firestore.
 */
(function () {
  "use strict";

  var NS = window.BSConfigurator;
  if (!NS) {
    console.error("[bs-configurator] Schema-Modul fehlt.");
    return;
  }

  // ---------------------------------------------------------------------------
  // Utils: dot-path get/set, escaping
  // ---------------------------------------------------------------------------
  function getPath(obj, path) {
    return path.split(".").reduce(function (o, k) {
      return o == null ? undefined : o[k];
    }, obj);
  }
  function setPath(obj, path, value) {
    var keys = path.split(".");
    var last = keys.pop();
    var target = keys.reduce(function (o, k) {
      if (o[k] == null) o[k] = {};
      return o[k];
    }, obj);
    target[last] = value;
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function formatInfoHtml(text) {
    return esc(text).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  }
  function el(html) {
    var t = document.createElement("template");
    t.innerHTML = html.trim();
    return t.content.firstElementChild;
  }

  // Meta Pixel custom events via Shopify Meta channel (`window.fbq`).
  // AddToCart is intentionally NOT fired here — Shopify maps cart/add → AddToCart.
  function trackMetaCustom(eventName, params) {
    try {
      if (typeof window === "undefined" || typeof window.fbq !== "function") return;
      window.fbq("trackCustom", eventName, params || {});
    } catch (e) {}
  }

  // Friction signals for the analytics listener (bs-clarity.js): only for things
  // that cannot be observed from the DOM (preview duration, upload rejections,
  // empty city search). Everything field- and step-related is derived from the
  // `data-bs-*` markers instead, so this stays deliberately small.
  // Tool-agnostic on purpose: name + params, no Clarity/Meta specifics here.
  function trackSignal(name, params) {
    try {
      if (typeof document === "undefined" || typeof window.CustomEvent !== "function") return;
      document.dispatchEvent(
        new CustomEvent("bs:track", { detail: { name: name, params: params || {} } })
      );
    } catch (e) {}
  }

  // Länderflagge als SVG-URL (flag-icons via jsDelivr, identisch zur Vue-App, wo
  // die flag-icons-NPM-Lib genutzt wird). code = ISO alpha-2 bzw. gb-eng/gb-sct/…
  function flagSvgUrl(code) {
    var c = String(code || "").trim().toLowerCase();
    return "https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/" + encodeURIComponent(c) + ".svg";
  }
  // Behutsame Namensbereinigung: nur Whitespace normalisieren (Runs -> ein
  // Leerzeichen, vorne/hinten trimmen). Ändert KEINE Zeichen und KEINE Groß-/
  // Kleinschreibung – Kundendaten bleiben inhaltlich unverändert. Analog zum
  // Trim, das die Vue-App auf Listen-Namen anwendet.
  function cleanText(v) {
    return String(v == null ? "" : v).replace(/\s+/g, " ").trim();
  }
  // base64 → Blob (wie firebase.js base64ToBlob), fuer den Storage-Upload des Previews
  function base64ToBlob(base64, mimeType) {
    var byteChars = atob(base64);
    var byteNumbers = new Array(byteChars.length);
    for (var i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType || "image/png" });
  }

  // Mapping wie geminiService.js
  function mapHairLength(v) {
    return (
      { "very-short": "very short", short: "short", medium: "medium", long: "long", "very-long": "very long" }[v] || v
    );
  }
  function ageGroup(birthday) {
    var a = NS.calcAge(birthday);
    if (a == null) return "8-10";
    if (a >= 5 && a <= 7) return "5-7";
    if (a >= 8 && a <= 10) return "8-10";
    if (a >= 11 && a <= 12) return "11-12";
    return a < 5 ? "5-7" : "8-10";
  }
  // Cover-Titel Zeile 1 ("Maxis ballstories®"), portiert aus
  // football-storybooks/src/lib/formatPossessive.js.
  function formatPossessive(name) {
    var n = (name || "").trim();
    if (!n || n === "Unbekannt") return "";
    if (/[sxzß]$/i.test(n)) return n;
    return n + "s";
  }
  function coverLine1(childName) {
    var n = (childName || "").trim();
    if (!n) return "Deine ballstories";
    var p = formatPossessive(n);
    return p.charAt(0).toUpperCase() + p.slice(1) + " ballstories";
  }
  // hairType UI→Storage (zopf→ponytail), wie book.js normalizeHairType
  function normalizeHairType(v) {
    if (!v) return "";
    var lower = String(v).toLowerCase();
    return lower === "zopf" ? "ponytail" : lower;
  }

  // City-Normalisierung — faithful zu football-storybooks/src/lib/book.js
  // (formatCitySelection + buildCityObject). Das Backend/environment-enricher
  // erwartet diese kanonische City-Form; Teilfelder werden mit denselben
  // Null-Defaults wie in der Vue-App aufgefuellt.
  function formatCitySelection(city) {
    if (!city || typeof city !== "object") return "";
    var parts = [city.name, city.admin1, city.country_code || city.country];
    return parts.filter(Boolean).join(", ");
  }

  function buildCityObject(cityDetails, cityString) {
    if (!cityDetails || typeof cityDetails !== "object") {
      return {
        displayName: cityString || "",
        source: "manual",
        id: "",
        name: cityString || "",
        admin1: "",
        admin1_id: null,
        admin2: null,
        admin2_id: null,
        admin3: null,
        admin3_id: null,
        admin4: null,
        admin4_id: null,
        country: "",
        country_code: "",
        country_id: null,
        latitude: null,
        longitude: null,
        elevation: null,
        timezone: "",
        population: null,
        feature_code: "",
      };
    }
    return {
      displayName: formatCitySelection(cityDetails),
      id: cityDetails.id || "",
      name: cityDetails.name || "",
      admin1: cityDetails.admin1 || "",
      admin1_id: cityDetails.admin1_id != null ? cityDetails.admin1_id : null,
      admin2: cityDetails.admin2 || null,
      admin2_id: cityDetails.admin2_id != null ? cityDetails.admin2_id : null,
      admin3: cityDetails.admin3 || null,
      admin3_id: cityDetails.admin3_id != null ? cityDetails.admin3_id : null,
      admin4: cityDetails.admin4 || null,
      admin4_id: cityDetails.admin4_id != null ? cityDetails.admin4_id : null,
      country: cityDetails.country || "",
      country_code: cityDetails.country_code || "",
      country_id: cityDetails.country_id != null ? cityDetails.country_id : null,
      latitude: cityDetails.latitude != null ? cityDetails.latitude : null,
      longitude: cityDetails.longitude != null ? cityDetails.longitude : null,
      elevation: cityDetails.elevation != null ? cityDetails.elevation : null,
      timezone: cityDetails.timezone || "",
      population: cityDetails.population != null ? cityDetails.population : null,
      feature_code: cityDetails.feature_code || "",
    };
  }

  // ---------------------------------------------------------------------------
  // Konfigurator-Instanz
  // ---------------------------------------------------------------------------
  function Configurator(root) {
    this.root = root;
    this.bookType = root.getAttribute("data-book-type") || "standard";
    this.variantId = root.getAttribute("data-variant-id") || "";
    this.appConfig = NS.mergeConfig(
      typeof window !== "undefined" ? window.BS_CONFIGURATOR_CONFIG : null
    );
    var infra = this.appConfig.infrastructure || {};
    this.fnBase = String(
      infra.functionsBase || root.getAttribute("data-functions-base") || "https://europe-west3-football-storybooks.cloudfunctions.net"
    ).replace(/\/$/, "");
    this.cartRedirect = String(infra.cartRedirect || root.getAttribute("data-cart-redirect") || "/checkout");
    this.storageBucket = String(
      infra.storageBucket || root.getAttribute("data-storage-bucket") || "football-storybooks.firebasestorage.app"
    );
    // Addon-Variant-IDs: zentral in App-Einstellungen; Block-Attribute nur Legacy-Fallback.
    this.addonAnecdoteVariantId = this._resolveAddonVariantId(
      "personal_anecdote",
      root.getAttribute("data-addon-anecdote-variant-id") || ""
    );
    this.dedicationVariantId = this._resolveAddonVariantId(
      "dedication",
      root.getAttribute("data-dedication-variant-id") || ""
    );
    this.specialEventVariantId = this._resolveAddonVariantId(
      "special_event",
      root.getAttribute("data-special-event-variant-id") || ""
    );
    this.addonPriceHints = {};
    // Folgebuch-Varianten: global in App-Einstellungen; Block-Attribut nur Legacy-Fallback.
    this.folgebuchVariants = [];
    var fvFromApp = this.appConfig.folgebuchVariants;
    if (Array.isArray(fvFromApp) && fvFromApp.length) {
      this.folgebuchVariants = fvFromApp.filter(function (v) {
        return v && v.book_type && v.variant_id;
      });
    }
    if (!this.folgebuchVariants.length) {
      try {
        var fvRaw = root.getAttribute("data-folgebuch-variants") || "";
        if (fvRaw.trim()) {
          var fvParsed = JSON.parse(fvRaw);
          if (Array.isArray(fvParsed)) {
            this.folgebuchVariants = fvParsed.filter(function (v) {
              return v && v.book_type && v.variant_id;
            });
          }
        }
      } catch (e) {
        this.folgebuchVariants = [];
      }
    }
    // Buch-Vorschau-Assets (Logo-Wasserzeichen + Silhouetten-Platzhalter), aus dem
    // Vue-Pendant (WatermarkedImage.vue / WmCreatePreviewAside.vue) portiert.
    this.logoUrl = root.getAttribute("data-logo-url") || "";
    this.silhouetteBoyUrl = root.getAttribute("data-silhouette-boy-url") || "";
    this.silhouetteGirlUrl = root.getAttribute("data-silhouette-girl-url") || "";
    this.mount = root.querySelector("[data-bs-mount]");

    this.schema = NS.buildSchema(this.bookType, {
      isFolgebuch: !!this.parentBookId,
      config: this.appConfig,
      addonAnecdoteVariantId: this.addonAnecdoteVariantId,
      dedicationVariantId: this.dedicationVariantId,
      specialEventVariantId: this.specialEventVariantId,
    });
    this.steps = this.schema.steps;
    this.form = NS.createEmptyForm();
    this._kosManual = false;
    this.stepIndex = 0;
    this.stepError = "";
    this.bookId = this._newBookId();
    // Eigene Session-ID fuer die Preview-Datei: Re-Generierungen ueberschreiben
    // dieselbe Storage-Datei (child-previews-temp/<sessionId>.png), wie in Vue.
    this.previewSessionId = this._newBookId();

    // Edit-Modus: gesetzt, wenn ein bestehendes Warenkorb-Buch bearbeitet wird.
    // Dann ersetzt der Submit dieselbe Cart-Zeile statt eine neue anzulegen.
    this.editLineKey = null;
    this._formHydrated = false;

    // Folgebuch-Modus: gesetzt, wenn aus einer frueheren Bestellung ein
    // Folgebuch angestossen wird (Customer-Account-Extension -> Token-Tresor).
    // Anders als der Edit-Modus entsteht ein NEUES Buch (neue bookId, neue
    // Cart-Zeile); parentBookId verknuepft es mit dem Vorgaengerbuch.
    this.parentBookId = null;

    // Preview-State
    this.previewLoading = false;
    this.previewError = "";
    this.previewFromCache = null;
    this._previewHintOpen = false;
    this._previewFeedbackOpen = false;
    // Einmal-Guard fuer den Vorschau-Autoload (siehe renderPreview).
    this.previewAutoTriggered = false;
    // Signatur der Aussehen-Felder zum Zeitpunkt der letzten Generierung. Weicht
    // die aktuelle Signatur ab, ist die Vorschau veraltet → Stale-Overlay.
    this.previewGeneratedSignature = null;

    // Shared inline feedback (preview mismatch + face-feature mismatch).
    this._feedbackOpen = "";
    this._feedbackSent = {};
    this._feedbackDraft = { email: "", message: "" };
    this._feedbackBusy = false;
    this._feedbackError = "";

    // Manual city fallback ("Deine Stadt ist nicht dabei?").
    this._cityManualOpen = false;
    this._cityManualReported = false;
    this._cityManualReportedText = "";

    // City-Autocomplete State
    this._cityTimer = null;
    this._citySuggestions = [];

    // In-page Edit-Event vom Warenkorb ("Buch ändern") verdrahten.
    this._wireEditEvents();

    // Bestehendes Warenkorb-Buch zum Bearbeiten? (Fallback via sessionStorage,
    // wenn der Konfigurator nicht auf der Warenkorbseite eingebettet ist.)
    this._maybeEnterEditMode();

    // Folgebuch aus einer frueheren Bestellung? (URL `?folgebuch=<token>`,
    // gesetzt von der Customer-Account-Extension; loest das Buch-Payload
    // asynchron aus dem Firebase-Token-Tresor ein.)
    this._maybeEnterFolgebuchMode();

    // Vereins-Landing? (URL `?club=<handle>`, gesetzt vom CTA der Club-
    // Metaobject-Seite). Befuellt Verein/Trikot/Stadt vor und merkt sich den
    // Landing-Kontext fuer den Payload (fromClubLanding/clubSlug/customerHighlights).
    this._maybeApplyClub();

    if (!this._formHydrated) {
      NS.applyAddonExpandDefaults(this.form, this._addonExpandCtx());
    }

    this._loadAddonPrices();

    this.render();
    this._wireMetaTracking();
  }

  // Emits a friction signal with the current step and book type attached, so the
  // listener never has to reach into the configurator state.
  Configurator.prototype._emit = function (name, params) {
    var step = this.steps[this.stepIndex];
    var payload = {
      book_type: this.bookType,
      step: step ? this.stepIndex + 1 + "_" + step.id : "",
    };
    if (params) {
      Object.keys(params).forEach(function (k) {
        payload[k] = params[k];
      });
    }
    trackSignal(name, payload);
  };

  Configurator.prototype._metaParams = function (extra) {
    var params = { book_type: this.bookType };
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        params[k] = extra[k];
      });
    }
    return params;
  };

  Configurator.prototype._trackMetaStart = function () {
    trackMetaCustom("ConfiguratorStart", this._metaParams());
    this._trackMetaStep();
  };

  Configurator.prototype._trackMetaStep = function () {
    var step = this.steps[this.stepIndex];
    trackMetaCustom(
      "ConfiguratorStep",
      this._metaParams({
        step: this.stepIndex + 1,
        step_id: step ? step.id : "",
        step_title: step ? step.title : "",
        total_steps: this.steps.length,
      })
    );
  };

  Configurator.prototype._trackMetaPreviewSeen = function (fromCache) {
    trackMetaCustom(
      "PreviewSeen",
      this._metaParams({ from_cache: !!fromCache })
    );
  };

  Configurator.prototype._trackMetaComplete = function () {
    trackMetaCustom("ConfiguratorComplete", this._metaParams());
  };

  // Fires ConfiguratorStart when the modal opens (or immediately for inline mode).
  Configurator.prototype._wireMetaTracking = function () {
    var self = this;
    var app = this.root.closest("[data-bs-app]");
    if (!app || app.getAttribute("data-display-mode") !== "modal") {
      this._trackMetaStart();
      return;
    }
    var dialog = app.querySelector("[data-bs-modal]");
    if (!dialog) return;
    var wasOpen = !!(dialog.open || dialog.hasAttribute("open"));
    if (wasOpen) this._trackMetaStart();
    var mo = new MutationObserver(function () {
      var isOpen = !!(dialog.open || dialog.hasAttribute("open"));
      if (isOpen && !wasOpen) self._trackMetaStart();
      wasOpen = isOpen;
    });
    mo.observe(dialog, { attributes: true, attributeFilter: ["open"] });
  };

  Configurator.prototype._shopifyRoot = function () {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  };

  Configurator.prototype._formatAddonPrice = function (raw) {
    var n = Number(raw);
    if (!isFinite(n)) return "";
    if (String(raw).indexOf(".") === -1 && n >= 100) n = n / 100;
    try {
      return (
        "+" +
        new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n)
      );
    } catch (e) {
      return "+" + n.toFixed(2).replace(".", ",") + " €";
    }
  };

  Configurator.prototype._addonCardTitle = function (field) {
    var title = field.label || "";
    var hint = field.addonId && this.addonPriceHints ? this.addonPriceHints[field.addonId] : "";
    return hint ? title + " (" + hint + ")" : title;
  };

  Configurator.prototype._loadAddonPrices = function () {
    var self = this;
    var map = {
      special_event: this.specialEventVariantId,
      personal_anecdote: this.addonAnecdoteVariantId,
      dedication: this.dedicationVariantId,
    };
    var pending = 0;
    Object.keys(map).forEach(function (addonId) {
      var variantId = String(map[addonId] || "").trim();
      if (!variantId) return;
      pending += 1;
      fetch(self._shopifyRoot() + "variants/" + encodeURIComponent(variantId) + ".js")
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (variant) {
          if (!variant || variant.price == null) return;
          var formatted = self._formatAddonPrice(variant.price);
          if (formatted) self.addonPriceHints[addonId] = formatted;
        })
        .catch(function () {})
        .finally(function () {
          pending -= 1;
          if (pending <= 0) self.render();
        });
    });
  };

  Configurator.prototype._addonExpandCtx = function () {
    var config = this.appConfig || {};
    return {
      bookType: this.bookType,
      config: config,
      dedicationEnabled:
        !!(config.featureFlags && config.featureFlags.dedicationEnabled) &&
        !!this.dedicationVariantId,
      specialEventEnabled:
        !!(config.featureFlags && config.featureFlags.specialEventEnabled) &&
        !!this.specialEventVariantId &&
        !!(config.addons && config.addons.special_event && config.addons.special_event.enabled !== false),
    };
  };

  Configurator.prototype._newBookId = function () {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return "bs-" + Date.now() + "-" + Math.random().toString(16).slice(2);
  };

  Configurator.prototype._resolveAddonVariantId = function (addonKey, legacyBlockValue) {
    var cfg =
      this.appConfig && this.appConfig.addons && this.appConfig.addons[addonKey]
        ? this.appConfig.addons[addonKey].variantId
        : "";
    return String(cfg || legacyBlockValue || "").trim();
  };

  Configurator.prototype._schemaOpts = function (bookType) {
    return {
      isFolgebuch: !!this.parentBookId,
      config: this.appConfig,
      addonAnecdoteVariantId: this.addonAnecdoteVariantId,
      dedicationVariantId: this.dedicationVariantId,
      specialEventVariantId: this.specialEventVariantId,
    };
  };

  // ---- sichtbare Schritte (showIf auf Step-Ebene gibt's nicht, nur Felder) ---
  Configurator.prototype.visibleFields = function (step) {
    var form = this.form;
    return step.fields.filter(function (f) {
      return !f.showIf || f.showIf(form);
    });
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  Configurator.prototype.render = function () {
    var step = this.steps[this.stepIndex];
    this.mount.innerHTML = "";

    // Club-Landing-Kontext: dezente, aber klar erkennbare Leiste ganz oben im
    // Modal, damit der Kunde sieht, dass er gerade ein Buch fuer "seinen" Verein
    // gestaltet (kommt vom CTA der Club-Landingpage, ?club=). Vereinslogo links,
    // Name fett. Wird bei jedem Render neu aufgebaut (Mount wird geleert).
    var cl = this.form._club;
    if (cl && cl.fromLanding) {
      var clubName =
        (cl.name && cl.name.trim()) ||
        (this.form.club.name && this.form.club.name.trim()) ||
        (cl.slug || "");
      var clubBar = el('<div class="bs-c__club-bar" role="note"></div>');
      if (cl.logoUrl) {
        clubBar.appendChild(
          el('<img class="bs-c__club-bar-logo" alt="" src="' + esc(cl.logoUrl) + '" />')
        );
      }
      clubBar.appendChild(
        el(
          '<span class="bs-c__club-bar-text">Du gestaltest gerade ein Buch für <strong>' +
            esc(clubName) +
            "</strong></span>"
        )
      );
      this.mount.appendChild(clubBar);
    }

    // Folgebuch-Kontext: dezente Leiste oben im Modal, damit der Kunde sieht,
    // dass er gerade ein Folgebuch zu einem bestehenden Buch gestaltet (kommt
    // vom Buecherregal, ?folgebuch=<token>). Titel des Vorgaengerbuchs aus dem
    // Token-Vault (parentTitle), rein informativ.
    if (this.parentBookId) {
      var fbTitle = (this.folgebuchTitle || "").trim();
      var fbBar = el('<div class="bs-c__folgebuch-bar" role="note"></div>');
      fbBar.appendChild(
        el(
          '<span class="bs-c__folgebuch-bar-text">' +
            (fbTitle
              ? 'Du gestaltest gerade ein <strong>Folgebuch</strong> zu „' +
                esc(fbTitle) +
                "“"
              : "Du gestaltest gerade ein <strong>Folgebuch</strong>") +
            "</span>"
        )
      );
      this.mount.appendChild(fbBar);

      // Varianten-Wahl (Klassisch/WM/…): nur im Folgebuch-Modus und nur wenn der
      // Merchant mehr als eine Variante gepflegt hat. Auswahl schaltet bookType +
      // variantId um (Cart-Line/Preis) und baut das Schema neu.
      if (this.folgebuchVariants.length > 1) {
        var fbVarSelf = this;
        var fbVarWrap = el('<div class="bs-c__field bs-c__field--chips"></div>');
        fbVarWrap.appendChild(
          el('<label class="bs-c__label">Variante</label>')
        );
        var fbVarGroup = el('<div class="bs-c__chips"></div>');
        this.folgebuchVariants.forEach(function (v) {
          var active = fbVarSelf.bookType === v.book_type;
          var chip = el(
            '<button type="button" class="bs-c__chip' +
              (active ? " is-active" : "") +
              '">' +
              esc(v.label || v.book_type) +
              "</button>"
          );
          chip.addEventListener("click", function () {
            if (fbVarSelf.bookType === v.book_type) return;
            fbVarSelf._applyFolgebuchVariant(v.book_type, v.variant_id);
            fbVarSelf.stepError = "";
            fbVarSelf.render();
          });
          fbVarGroup.appendChild(chip);
        });
        fbVarWrap.appendChild(fbVarGroup);
        this.mount.appendChild(fbVarWrap);
      }
    }

    // Fortschritt
    var total = this.steps.length;
    this.mount.appendChild(
      el(
        '<div class="bs-c__progress"><span>Schritt ' +
          (this.stepIndex + 1) +
          " von " +
          total +
          '</span><div class="bs-c__bar"><i style="width:' +
          Math.round(((this.stepIndex + 1) / total) * 100) +
          '%"></i></div></div>'
      )
    );

    var section = el('<section class="bs-c__step"></section>');
    // Analytics markers (read by bs-clarity.js). Never contain customer input.
    section.setAttribute("data-bs-step", this.stepIndex + 1 + "_" + step.id);
    section.setAttribute("data-bs-step-total", this.steps.length);
    section.setAttribute("data-bs-book-type", this.bookType);
    if (this.stepError && this.errorFieldKey) {
      section.setAttribute("data-bs-error-field", this.errorFieldKey);
    }
    if (step.lead) {
      section.appendChild(el('<p class="bs-c__lead">' + esc(step.lead) + "</p>"));
    }
    section.appendChild(el('<h2 class="bs-c__title">' + esc(step.title) + "</h2>"));

    var self = this;
    this.visibleFields(step).forEach(function (field) {
      section.appendChild(self.renderField(field));
    });

    // Abschluss-Schritt: vollständige Review-Übersicht mit Ändern-Sprung.
    if (step.isFinal) {
      section.appendChild(this.renderReview());
    }

    // Fehler
    if (this.stepError) {
      section.appendChild(el('<p class="bs-c__error">' + esc(this.stepError) + "</p>"));
    }

    // Aktionen
    section.appendChild(this.renderActions(step));
    this.mount.appendChild(section);
    this._syncFeedbackSheet();
  };

  // ---------------------------------------------------------------------------
  // FELD-RENDERER
  // ---------------------------------------------------------------------------
  Configurator.prototype.renderField = function (field) {
    var node;
    switch (field.type) {
      case "chips":
        node = this.renderChips(field);
        break;
      case "select":
        node = this.renderSelect(field);
        break;
      case "text":
        node = this.renderText(field);
        break;
      case "textarea":
        node = this.renderTextarea(field);
        break;
      case "file":
        node = this.renderFile(field);
        break;
      case "date":
        node = this.renderDate(field);
        break;
      case "checkbox":
        node = this.renderCheckbox(field);
        break;
      case "addonCard":
        node = this.renderAddonCard(field);
        break;
      case "city":
        node = this.renderCity(field);
        break;
      case "list":
        node = this.renderList(field);
        break;
      case "preview":
        node = this.renderPreview(field);
        break;
      default:
        node = el('<div class="bs-c__field">Unbekanntes Feld: ' + esc(field.type) + "</div>");
    }
    // Analytics markers (read by bs-clarity.js): identify the field without
    // exposing its value. `field.path` is a stable schema identifier.
    if (node && field.path) {
      node.setAttribute("data-bs-field", field.path);
      node.setAttribute("data-bs-field-type", field.type);
      if (field.required) node.setAttribute("data-bs-required", "1");
    }
    // Inline-Fehler: betroffenes Feld hervorheben + Meldung direkt darunter
    // (zusätzlich zur Sammelmeldung am Weiter-Button). errorFieldKey wird in
    // validateStep gesetzt; stepError trägt den Text.
    if (node && this.stepError && this.errorFieldKey && field.path === this.errorFieldKey) {
      node.classList.add("is-error");
      node.appendChild(el('<p class="bs-c__field-error">' + esc(this.stepError) + "</p>"));
    }
    return node;
  };

  // Klick ausserhalb schliesst offene Info-Popover. Beim Fragetext-Trigger liegt
  // das Popover über dem Eingabefeld, in das der Kunde direkt danach tippen will –
  // ohne das hier müsste er zweimal klicken.
  //
  // Capture-Phase ist Pflicht, nicht Geschmackssache: Im Modal-Modus stoppt
  // bs-modal.js auf .bs-modal__inner jeden Klick (damit der erste Klick nicht den
  // Dialog schliesst). In der Bubble-Phase erreicht daher KEIN Klick aus dem
  // Konfigurator das document – das Popover liesse sich nur über seinen eigenen
  // Trigger wieder schliessen.
  function ensureInfoOutsideClose() {
    if (document.__bsInfoOutsideClose) return;
    document.__bsInfoOutsideClose = true;
    document.addEventListener(
      "click",
      function (e) {
        var open = document.querySelectorAll(".bs-c__info[open]");
        if (!open.length) return;
        open.forEach(function (node) {
          if (!node.contains(e.target)) node.removeAttribute("open");
        });
      },
      true
    );
  }

  // opts.triggerLabel: Fragetext als Auslöser statt des "i"-Icons. Bei den als
  // sensibel empfundenen Pflichtfeldern (Geburtstag, Nachname) wird das Icon zu
  // oft überlesen, obwohl dort die Erklärung über den Abbruch entscheidet.
  // opts.title: fett vorangestellte Kernaussage – viele lesen nur die erste Zeile.
  Configurator.prototype._infoDetailsEl = function (infoText, opts) {
    var self = this;
    opts = opts || {};
    ensureInfoOutsideClose();
    var isLink = !!opts.triggerLabel;
    var details = el(
      '<details class="bs-c__info' +
        (isLink ? " bs-c__info--link" : "") +
        '">' +
        '<summary class="bs-c__info-trigger' +
        (isLink ? " bs-c__info-trigger--link" : "") +
        '"' +
        (isLink ? "" : ' aria-label="Mehr Informationen"') +
        ">" +
        (isLink ? esc(opts.triggerLabel) : "i") +
        "</summary>" +
        '<div class="bs-c__info-content">' +
        (opts.title ? '<strong class="bs-c__info-title">' + esc(opts.title) + "</strong>" : "") +
        formatInfoHtml(infoText) +
        "</div>" +
        "</details>"
    );
    var summary = details.querySelector("summary");
    if (summary) {
      summary.addEventListener("click", function (e) {
        e.stopPropagation();
      });
    }
    details.addEventListener("toggle", function () {
      if (!details.open) {
        details.classList.remove("is-flip-y");
        details.style.setProperty("--bs-info-shift", "0px");
        return;
      }
      self._closeOtherInfoPopovers(details);
      self._positionInfoPopover(details);
    });
    if (opts.action && opts.action.label && opts.action.type) {
      var content = details.querySelector(".bs-c__info-content");
      if (content) {
        var actionRow = el('<p class="bs-c__info-action"></p>');
        if (opts.action.prefix) actionRow.appendChild(document.createTextNode(opts.action.prefix));
        var actionBtn = el(
          '<button type="button" class="bs-c__info-action-link">' + esc(opts.action.label) + "</button>"
        );
        actionBtn.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          details.removeAttribute("open");
          self._openFeedback(opts.action.type);
        });
        actionRow.appendChild(actionBtn);
        if (opts.action.suffix) actionRow.appendChild(document.createTextNode(opts.action.suffix));
        content.appendChild(actionRow);
      }
    }
    return details;
  };

  Configurator.prototype._closeOtherInfoPopovers = function (keepDetails) {
    document.querySelectorAll(".bs-c__info[open]").forEach(function (node) {
      if (node !== keepDetails) node.removeAttribute("open");
    });
  };

  Configurator.prototype._positionInfoPopover = function (details) {
    var content = details && details.querySelector(".bs-c__info-content");
    if (!content) return;
    details.classList.remove("is-flip-y");
    details.style.setProperty("--bs-info-shift", "0px");

    var viewportPad = 16;
    var boundary = details.closest(".bs-modal__inner");
    var boundaryRect = boundary
      ? boundary.getBoundingClientRect()
      : { left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight, width: window.innerWidth };
    var maxWidth = Math.max(180, Math.min(320, Math.floor(boundaryRect.width - viewportPad * 2)));
    details.style.setProperty("--bs-info-max-width", maxWidth + "px");

    window.requestAnimationFrame(function () {
      var rect = content.getBoundingClientRect();
      var shift = 0;
      if (rect.left < boundaryRect.left + viewportPad) shift += boundaryRect.left + viewportPad - rect.left;
      if (rect.right > boundaryRect.right - viewportPad) shift -= rect.right - (boundaryRect.right - viewportPad);
      details.style.setProperty("--bs-info-shift", shift + "px");

      // Wenn nach unten nicht genug Platz ist, oberhalb öffnen (v. a. mobil/im Modal).
      rect = content.getBoundingClientRect();
      var triggerRect = details.getBoundingClientRect();
      var spaceBelow = boundaryRect.bottom - viewportPad - triggerRect.bottom;
      var spaceAbove = triggerRect.top - (boundaryRect.top + viewportPad);
      if (rect.height > spaceBelow && spaceAbove > spaceBelow) {
        details.classList.add("is-flip-y");
      }
    });
  };

  Configurator.prototype._labelEl = function (field) {
    // Stern bei ALLEN Pflichtfeldern – inkl. Pflicht-Listen (min>=1), die kein
    // eigenes `required:true` tragen (Trainer/Mitspieler).
    var isRequired = field.required || field.min >= 1;
    var label = el(
      '<label class="bs-c__label">' +
        esc(field.label) +
        (isRequired ? ' <span class="bs-c__req">*</span>' : "") +
        "</label>"
    );
    if (!field.info) return label;
    var details = this._infoDetailsEl(field.info, {
      triggerLabel: field.infoLabel,
      title: field.infoTitle,
      action: field.infoAction,
    });
    // Der Fragetext braucht eine eigene Zeile unter dem Label – neben dem Label
    // würde er auf schmalen Displays mit ihm verschmelzen.
    if (field.infoLabel) {
      var block = el('<div class="bs-c__label-block"></div>');
      block.appendChild(label);
      block.appendChild(details);
      return block;
    }
    var row = el('<div class="bs-c__label-row"></div>');
    row.appendChild(label);
    row.appendChild(details);
    return row;
  };

  Configurator.prototype._appearanceSnapshot = function () {
    var c = this.form.child;
    return {
      gender: c.gender || "",
      hairColor: c.hairColor || "",
      hairLength: c.hairLength || "",
      hairType: c.hairType || "",
      eyeColor: c.eyeColor || "",
      skinColor: c.skinColor || "",
      faceFeatures: c.faceFeatures || "",
      wearsGlasses: !!c.wearsGlasses,
      ageGroup: ageGroup(c.birthday),
      signature: this._previewSignature(),
    };
  };

  Configurator.prototype._previewHttpUrl = function () {
    var c = this.form.child;
    var url = c.previewImageUrl || "";
    if (url.indexOf("http") === 0) return url;
    var cover = c.previewCoverImageUrl || "";
    if (cover.indexOf("http") === 0) return cover;
    if (!this.previewSessionId || !this.storageBucket) return "";
    return (
      "https://firebasestorage.googleapis.com/v0/b/" +
      this.storageBucket +
      "/o/" +
      encodeURIComponent("child-previews-temp/" + this.previewSessionId + ".png") +
      "?alt=media"
    );
  };

  Configurator.prototype._openFeedback = function (type) {
    this._feedbackOpen = type;
    this._feedbackError = "";
    this._syncFeedbackSheet();
  };

  Configurator.prototype._closeFeedback = function () {
    this._feedbackOpen = "";
    this._feedbackError = "";
    this._previewFeedbackOpen = false;
    this._syncFeedbackSheet();
  };

  Configurator.prototype._feedbackHost = function () {
    return this.root.closest("dialog.bs-modal") || this.root;
  };

  Configurator.prototype._syncFeedbackSheet = function () {
    var host = this._feedbackHost();
    var old = host.querySelector(".bs-c__sheet");
    if (old) old.parentNode.removeChild(old);
    if (!this._feedbackOpen) return;
    host.appendChild(this._renderFeedbackSheet(this._feedbackOpen));
  };

  Configurator.prototype._renderFeedbackSheet = function (type) {
    var self = this;
    var sheet = el('<div class="bs-c__sheet" role="dialog" aria-modal="true"></div>');
    var card = el('<div class="bs-c__sheet-card"></div>');
    var title =
      type === "preview_mismatch" ? "Passt die Vorschau nicht?" : "Passt die Illustration nicht?";
    var head = el('<div class="bs-c__sheet-head"></div>');
    head.appendChild(el('<h3 class="bs-c__sheet-title">' + esc(title) + "</h3>"));
    var close = el('<button type="button" class="bs-c__sheet-close" aria-label="Schließen">×</button>');
    close.addEventListener("click", function () {
      self._closeFeedback();
    });
    head.appendChild(close);
    card.appendChild(head);
    card.appendChild(this._renderFeedbackForm(type));
    sheet.appendChild(card);
    sheet.addEventListener("click", function (e) {
      if (e.target === sheet) self._closeFeedback();
    });
    return sheet;
  };

  Configurator.prototype._renderFeedbackForm = function (type) {
    var self = this;
    var wrap = el('<div class="bs-c__feedback" data-bs-feedback="' + esc(type) + '"></div>');
    if (this._feedbackSent[type]) {
      wrap.appendChild(el('<p class="bs-c__feedback-thanks">Danke – wir melden uns!</p>'));
      return wrap;
    }
    wrap.appendChild(el('<label class="bs-c__label">E-Mail <span class="bs-c__req">*</span></label>'));
    var email = el('<input type="email" class="bs-c__input" autocomplete="email" placeholder="deine@email.de" />');
    email.value = this._feedbackDraft.email || "";
    email.addEventListener("input", function () {
      self._feedbackDraft.email = email.value;
    });
    wrap.appendChild(email);
    wrap.appendChild(el('<label class="bs-c__label">Was passt nicht? <span class="bs-c__req">*</span></label>'));
    var ta = el('<textarea class="bs-c__input" rows="3" maxlength="300" placeholder="Kurz beschreiben …"></textarea>');
    ta.value = this._feedbackDraft.message || "";
    var counter = el('<p class="bs-c__hint bs-c__charcount"></p>');
    function syncCount() {
      counter.textContent = (ta.value || "").length + " / 300";
    }
    ta.addEventListener("input", function () {
      self._feedbackDraft.message = ta.value;
      syncCount();
    });
    wrap.appendChild(ta);
    syncCount();
    wrap.appendChild(counter);
    var btn = el('<button type="button" class="bs-c__btn bs-c__btn--secondary">Senden</button>');
    if (this._feedbackBusy) btn.disabled = true;
    btn.addEventListener("click", function () {
      self._submitFeedback(type);
    });
    wrap.appendChild(btn);
    if (this._feedbackError) {
      wrap.appendChild(el('<p class="bs-c__field-error">' + esc(this._feedbackError) + "</p>"));
    }
    return wrap;
  };

  Configurator.prototype._submitFeedback = function (type) {
    var self = this;
    var email = String((this._feedbackDraft && this._feedbackDraft.email) || "").trim();
    var message = String((this._feedbackDraft && this._feedbackDraft.message) || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._feedbackError = "Bitte eine gültige E-Mail angeben.";
      this._syncFeedbackSheet();
      return;
    }
    if (!message) {
      this._feedbackError = "Bitte kurz beschreiben, was nicht passt.";
      this._syncFeedbackSheet();
      return;
    }
    this._feedbackBusy = true;
    this._feedbackError = "";
    this._syncFeedbackSheet();
    fetch(this.fnBase + "/submitConfiguratorFeedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: type,
        email: email,
        message: message.slice(0, 300),
        appearanceSnapshot: this._appearanceSnapshot(),
        fromCache: type === "preview_mismatch" ? !!this.previewFromCache : undefined,
        bookId: this.bookId || "",
        previewSessionId: this.previewSessionId || "",
        previewImageUrl: this._previewHttpUrl(),
      }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.error || "HTTP " + res.status);
          return data;
        });
      })
      .then(function () {
        self._feedbackBusy = false;
        self._feedbackSent[type] = true;
        self._feedbackDraft = { email: "", message: "" };
        self._previewFeedbackOpen = false;
        self._syncFeedbackSheet();
      })
      .catch(function (e) {
        self._feedbackBusy = false;
        self._feedbackError = (e && e.message) || "Senden fehlgeschlagen.";
        self._syncFeedbackSheet();
      });
  };

  Configurator.prototype.renderChips = function (field) {
    var self = this;
    var form = this.form;
    var disabled = field.disabledIf && field.disabledIf(form);
    var current = getPath(form, field.path);
    if (field.path === "child.kindergartenOrSchool" && !self._kosManual) {
      var suggested = NS.suggestKindergartenOrSchool(NS.calcAge(form.child.birthday));
      if (suggested) {
        setPath(form, field.path, suggested);
        current = suggested;
      }
    }
    var wrap = el('<div class="bs-c__field bs-c__field--chips' + (disabled ? " is-disabled" : "") + '"></div>');
    wrap.appendChild(this._labelEl(field));
    if (field.hint) wrap.appendChild(el('<p class="bs-c__hint">' + esc(field.hint) + "</p>"));
    var group = el('<div class="bs-c__chips"></div>');
    // Optionaler "Ohne …"-Chip (Default): repräsentiert null. Aktiv, solange nichts
    // gewählt ist. Ersetzt den früheren Skip-Button (Events/Werte sind optional).
    if (field.noneOption) {
      var noneActive = current == null || current === "";
      var noneChip = el(
        '<button type="button" class="bs-c__chip bs-c__chip--none' + (noneActive ? " is-active" : "") + '">' +
          '<span class="bs-c__chip-emoji" aria-hidden="true">🚫</span>' + esc(field.noneOption) + "</button>"
      );
      if (disabled) noneChip.disabled = true;
      noneChip.addEventListener("click", function () {
        setPath(form, field.path, null);
        self.stepError = "";
        self.render();
      });
      group.appendChild(noneChip);
    }
    field.options.forEach(function (opt) {
      var active = current === opt.value;
      var emojiHtml = opt.emoji ? '<span class="bs-c__chip-emoji" aria-hidden="true">' + esc(opt.emoji) + "</span>" : "";
      var chip = el(
        '<button type="button" class="bs-c__chip' + (active ? " is-active" : "") + '" data-value="' + esc(opt.value) + '">' + emojiHtml + esc(opt.label) + "</button>"
      );
      if (disabled) chip.disabled = true;
      chip.addEventListener("click", function () {
        if (field.path === "child.kindergartenOrSchool") self._kosManual = true;
        // nullable: erneuter Klick auf aktiven Wert → null (Toggle, wie Vue referralSource/events)
        if (field.nullable && current === opt.value) {
          setPath(form, field.path, null);
        } else {
          setPath(form, field.path, opt.value);
        }
        self.stepError = "";
        self.render();
      });
      group.appendChild(chip);
    });
    wrap.appendChild(group);
    return wrap;
  };

  // Gruppiertes Dropdown (<select> mit <optgroup>) – für lange Optionslisten wie
  // die WM-Nationalmannschaften. Zeigt links die Flagge des gewählten Landes.
  Configurator.prototype.renderSelect = function (field) {
    var self = this;
    var form = this.form;
    var current = getPath(form, field.path) || "";
    var wrap = el('<div class="bs-c__field bs-c__field--select"></div>');
    wrap.appendChild(this._labelEl(field));
    if (field.hint) wrap.appendChild(el('<p class="bs-c__hint">' + esc(field.hint) + "</p>"));

    var row = el('<div class="bs-c__select-row"></div>');
    // Flagge links neben dem Dropdown (nur wenn ein Land gewählt ist) – wie Vue.
    if (current) {
      var flag = el('<span class="bs-c__select-flag" aria-hidden="true"></span>');
      flag.style.backgroundImage = "url(" + flagSvgUrl(current) + ")";
      row.appendChild(flag);
    }

    var sel = el('<select class="bs-c__select"></select>');
    sel.appendChild(el('<option value="">' + esc(field.placeholder || "Bitte wählen …") + "</option>"));
    // Optionen nach group in <optgroup> bündeln (Reihenfolge des Katalogs).
    var order = [];
    var byGroup = {};
    (field.options || []).forEach(function (opt) {
      var g = opt.group || "Weitere";
      if (!byGroup[g]) {
        byGroup[g] = [];
        order.push(g);
      }
      byGroup[g].push(opt);
    });
    order.forEach(function (g) {
      var og = el('<optgroup label="' + esc(g) + '"></optgroup>');
      byGroup[g].forEach(function (opt) {
        var o = el('<option value="' + esc(opt.value) + '">' + esc(opt.label) + "</option>");
        if (String(opt.value) === String(current)) o.selected = true;
        og.appendChild(o);
      });
      sel.appendChild(og);
    });
    sel.value = current;
    sel.addEventListener("change", function () {
      setPath(form, field.path, sel.value || null);
      self.stepError = "";
      self.render();
    });
    row.appendChild(sel);
    wrap.appendChild(row);
    return wrap;
  };

  // Live-Zeichenzähler: genutzt / Limit + Rest (z. B. "45 / 120 · noch 75").
  function formatCharCount(len, max) {
    var used = Math.min(Number(len) || 0, max);
    return used + " / " + max + " · noch " + Math.max(0, max - used);
  }

  function insertTextAtCursor(textarea, insert, maxLen) {
    var val = textarea.value || "";
    var start = typeof textarea.selectionStart === "number" ? textarea.selectionStart : val.length;
    var end = typeof textarea.selectionEnd === "number" ? textarea.selectionEnd : start;
    var next = val.slice(0, start) + insert + val.slice(end);
    if (maxLen && next.length > maxLen) next = next.slice(0, maxLen);
    textarea.value = next;
    var pos = Math.min(start + insert.length, next.length);
    try {
      textarea.setSelectionRange(pos, pos);
    } catch (e) {}
    textarea.focus();
    return next;
  }

  Configurator.prototype.renderText = function (field) {
    var self = this;
    var wrap = el('<div class="bs-c__field"></div>');
    wrap.appendChild(this._labelEl(field));
    if (field.hint) wrap.appendChild(el('<p class="bs-c__hint">' + esc(field.hint) + "</p>"));
    var input = el('<input type="text" class="bs-c__input" />');
    input.value = getPath(this.form, field.path) || "";
    if (field.placeholder) input.placeholder = field.placeholder;
    if (field.maxlength) input.maxLength = field.maxlength;
    if (field.inputmode) input.inputMode = field.inputmode;
    if (field.pattern) input.pattern = field.pattern;
    var counter = field.maxlength ? el('<p class="bs-c__hint bs-c__charcount"></p>') : null;
    function syncCount() {
      if (counter) counter.textContent = formatCharCount((input.value || "").length, field.maxlength);
    }
    input.addEventListener("input", function () {
      setPath(self.form, field.path, input.value);
      syncCount();
      // Live-Titel-Update (Vorname → Buch-Teaser auf Seite 1), gezielt ohne
      // Full-Re-Render, damit das Eingabefeld den Fokus behaelt.
      if (field.syncBookTitle) {
        var titles = self.root.querySelectorAll("[data-bs-book-title]");
        for (var i = 0; i < titles.length; i++) {
          titles[i].textContent = coverLine1(input.value);
        }
      }
    });
    wrap.appendChild(input);
    if (counter) {
      syncCount();
      wrap.appendChild(counter);
    }
    return wrap;
  };

  // Mehrzeiliges Textfeld mit Live-Zeichenzähler (z. B. Widmung, max. 300 Zeichen).
  Configurator.prototype.renderTextarea = function (field) {
    var self = this;
    var wrap = el('<div class="bs-c__field"></div>');
    wrap.appendChild(this._labelEl(field));
    if (field.hint) wrap.appendChild(el('<p class="bs-c__hint">' + esc(field.hint) + "</p>"));
    var ta = el('<textarea class="bs-c__input bs-c__textarea" rows="4"></textarea>');
    ta.value = getPath(this.form, field.path) || "";
    if (field.placeholder) ta.placeholder = field.placeholder;
    if (field.maxlength) ta.maxLength = field.maxlength;
    var counter = field.maxlength ? el('<p class="bs-c__hint bs-c__charcount"></p>') : null;
    function syncCount() {
      if (counter) counter.textContent = formatCharCount((ta.value || "").length, field.maxlength);
    }
    function commitValue() {
      setPath(self.form, field.path, ta.value);
      syncCount();
    }
    ta.addEventListener("input", commitValue);

    if (Array.isArray(field.emojis) && field.emojis.length) {
      if (field.emojiHint) {
        wrap.appendChild(el('<p class="bs-c__hint bs-c__emoji-hint">' + esc(field.emojiHint) + "</p>"));
      }
      var bar = el('<div class="bs-c__emoji-bar" role="toolbar" aria-label="Emojis einfügen"></div>');
      field.emojis.forEach(function (emoji) {
        if (!emoji) return;
        var btn = el(
          '<button type="button" class="bs-c__emoji-btn" aria-label="' + esc("Emoji " + emoji + " einfügen") + '">' + emoji + "</button>"
        );
        btn.addEventListener("click", function () {
          var next = insertTextAtCursor(ta, emoji, field.maxlength);
          setPath(self.form, field.path, next);
          syncCount();
        });
        bar.appendChild(btn);
      });
      wrap.appendChild(bar);
    }

    wrap.appendChild(ta);
    if (counter) {
      syncCount();
      wrap.appendChild(counter);
    }
    return wrap;
  };

  // Bild-Upload: lädt sofort nach Firebase Storage (wie Preview) und speichert die
  // zurückgegebene URL im Form-State. Es wird NIE das rohe Base64 in den Payload
  // geschrieben (vgl. Preview-Upload). Zeigt eine kleine Vorschau + Entfernen-Button.
  Configurator.prototype.renderFile = function (field) {
    var self = this;
    var wrap = el('<div class="bs-c__field"></div>');
    wrap.appendChild(this._labelEl(field));
    if (field.hint) wrap.appendChild(el('<p class="bs-c__hint">' + esc(field.hint) + "</p>"));
    var current = getPath(this.form, field.path) || "";
    var status = el('<p class="bs-c__hint bs-c__file-status"></p>');
    // Verstecktes native Input – die Dropzone triggert es per Klick.
    var input = el('<input type="file" accept="image/*" class="bs-c__file-input-hidden" />');
    var dropzone = el('<div class="bs-c__dropzone" role="button" tabindex="0" aria-label="Widmungs-Foto hochladen"></div>');

    var DZ_ICON =
      '<svg class="bs-c__dropzone-icon" viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>';

    function renderDefault() {
      dropzone.classList.remove("has-preview");
      dropzone.innerHTML =
        DZ_ICON +
        '<p class="bs-c__dropzone-title">Foto hierher ziehen</p>' +
        '<p class="bs-c__dropzone-or">oder</p>' +
        '<button type="button" class="bs-c__btn bs-c__btn--secondary bs-c__dropzone-btn">Foto auswählen</button>' +
        '<p class="bs-c__dropzone-hint">JPG oder PNG · max. 8 MB</p>';
    }

    function showPreview(url) {
      if (!url) {
        renderDefault();
        return;
      }
      dropzone.classList.add("has-preview");
      dropzone.innerHTML = "";
      var img = el('<img class="bs-c__file-thumb" alt="Widmungs-Foto" />');
      img.src = url;
      dropzone.appendChild(img);
      var rm = el('<button type="button" class="bs-c__btn bs-c__btn--ghost bs-c__file-remove">Foto entfernen</button>');
      rm.addEventListener("click", function (e) {
        e.stopPropagation(); // nicht den Datei-Dialog erneut öffnen
        setPath(self.form, field.path, "");
        status.textContent = "";
        input.value = "";
        showPreview("");
      });
      dropzone.appendChild(rm);
    }

    var consentPath = field.consentPath;
    function hasConsent() {
      return !consentPath || !!getPath(self.form, consentPath);
    }

    function handleFile(file) {
      if (!file) return;
      if (!hasConsent()) {
        status.textContent = "Bitte zuerst die Bestätigung ankreuzen.";
        self._emit("upload_blocked", { field: field.path, reason: "no_consent" });
        return;
      }
      if (!/^image\//.test(file.type)) {
        status.textContent = "Bitte ein Bild auswählen (JPG/PNG).";
        self._emit("upload_blocked", { field: field.path, reason: "wrong_type" });
        return;
      }
      if (file.size > 8 * 1024 * 1024) {
        status.textContent = "Bild ist zu groß (max. 8 MB).";
        self._emit("upload_blocked", { field: field.path, reason: "too_large" });
        return;
      }
      self._emit("upload_start", { field: field.path });
      dropzone.classList.add("is-uploading");
      status.textContent = "Foto wird hochgeladen …";
      var reader = new FileReader();
      reader.onload = function () {
        var base64 = String(reader.result || "").split(",")[1] || "";
        var ext = (file.type.split("/")[1] || "png").replace("jpeg", "jpg");
        var objectPath =
          "dedication-temp/" + (self.previewSessionId || self.bookId || "anon") + "-" + Date.now() + "." + ext;
        self
          .uploadPreview(base64, objectPath, file.type)
          .then(function (url) {
            setPath(self.form, field.path, url);
            status.textContent = "Foto hochgeladen ✓";
            dropzone.classList.remove("is-uploading");
            showPreview(url);
            self._emit("upload_done", { field: field.path });
          })
          .catch(function (e) {
            status.textContent = "Upload fehlgeschlagen – bitte erneut versuchen.";
            dropzone.classList.remove("is-uploading");
            console.warn("[bs-configurator] Widmungs-Foto-Upload:", e && e.message);
            self._emit("upload_error", { field: field.path });
          });
      };
      reader.readAsDataURL(file);
    }

    input.addEventListener("change", function () {
      handleFile(input.files && input.files[0]);
    });

    function lockedGuard() {
      // true = Aktion blockiert (kein Consent). Hinweis anzeigen.
      if (hasConsent()) return false;
      status.textContent = "Bitte zuerst die Bestätigung ankreuzen.";
      self._emit("upload_blocked", { field: field.path, reason: "no_consent" });
      return true;
    }

    // Klick/Tastatur auf die Dropzone öffnet den Datei-Dialog (außer im Preview-Zustand,
    // da übernimmt der „Foto entfernen“-Button per stopPropagation; und nur mit Consent).
    dropzone.addEventListener("click", function () {
      if (dropzone.classList.contains("has-preview")) return;
      if (lockedGuard()) return;
      input.click();
    });
    dropzone.addEventListener("keydown", function (e) {
      if ((e.key === "Enter" || e.key === " ") && !dropzone.classList.contains("has-preview")) {
        e.preventDefault();
        if (lockedGuard()) return;
        input.click();
      }
    });

    // Drag & Drop
    ["dragenter", "dragover"].forEach(function (evt) {
      dropzone.addEventListener(evt, function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!dropzone.classList.contains("has-preview")) dropzone.classList.add("is-dragover");
      });
    });
    ["dragleave", "dragend"].forEach(function (evt) {
      dropzone.addEventListener(evt, function (e) {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.remove("is-dragover");
      });
    });
    dropzone.addEventListener("drop", function (e) {
      e.preventDefault();
      e.stopPropagation();
      dropzone.classList.remove("is-dragover");
      var dt = e.dataTransfer;
      handleFile(dt && dt.files && dt.files[0]);
    });

    // Pflicht-Bestätigung (Rechte/Datenschutz): ohne Haken kein Upload möglich.
    function applyLockState() {
      if (hasConsent()) dropzone.classList.remove("is-locked");
      else dropzone.classList.add("is-locked");
    }
    if (consentPath) {
      var consentWrap = el('<label class="bs-c__file-consent"></label>');
      var cb = el('<input type="checkbox" class="bs-c__file-consent-box" />');
      cb.checked = !!getPath(this.form, consentPath);
      cb.addEventListener("change", function () {
        setPath(self.form, consentPath, cb.checked);
        if (!cb.checked) {
          // Bestätigung zurückgezogen → vorhandenes Foto entfernen.
          setPath(self.form, field.path, "");
          input.value = "";
          showPreview("");
          status.textContent = "";
        }
        applyLockState();
      });
      consentWrap.appendChild(cb);
      consentWrap.appendChild(el('<span class="bs-c__file-consent-text">' + esc(field.disclaimer || "") + "</span>"));
      wrap.appendChild(consentWrap);
    } else if (field.disclaimer) {
      wrap.appendChild(el('<p class="bs-c__file-disclaimer">' + esc(field.disclaimer) + "</p>"));
    }

    wrap.appendChild(input);
    wrap.appendChild(dropzone);
    wrap.appendChild(status);
    showPreview(current);
    applyLockState();
    return wrap;
  };

  Configurator.prototype.renderDate = function (field) {
    var self = this;
    var wrap = el('<div class="bs-c__field"></div>');
    wrap.appendChild(this._labelEl(field));
    var input = el('<input type="date" class="bs-c__input" />');
    input.value = getPath(this.form, field.path) || "";
    var ageHint = el('<p class="bs-c__hint"></p>');
    function syncHint() {
      var a = NS.calcAge(getPath(self.form, field.path));
      // Bewusst als "Sprachstufe" statt "Alter" formuliert: das berechnete Alter steuert
      // nur die CEFR-Sprachanalyse und taucht NIE im Buch auf. Das reduziert Rückfragen
      // ("mein Kind wird 6, im Buch steht 5"), ohne einen fetten Warnhinweis am Feld.
      ageHint.textContent =
        a !== null && a >= 0 ? "Sprachstufe: " + a + " Jahre · erscheint nicht im Buch" : "";
    }
    // Live nur State + Alters-Hinweis aktualisieren – KEIN render() pro
    // Tastendruck. Das Date-Input feuert "change" bereits waehrend der
    // Jahr-Eingabe (sobald alle Segmente valide sind); ein render() dort
    // wuerde das Element neu bauen und den Fokus stehlen ("fang von vorn an").
    input.addEventListener("input", function () {
      setPath(self.form, field.path, input.value);
      syncHint();
      self._applyKindergartenOrSchoolDefault();
    });
    input.addEventListener("change", function () {
      setPath(self.form, field.path, input.value);
      syncHint();
      self._applyKindergartenOrSchoolDefault();
    });
    // Kein render() während der Eingabe: das Date-Input feuert change schon
    // sobald alle Segmente valide sind und würde sonst den Fokus stehlen.
    wrap.appendChild(input);
    syncHint();
    wrap.appendChild(ageHint);
    return wrap;
  };

  // Wählt Kindergarten/Schule nach Alter, ohne das Datum-Feld neu zu bauen.
  // Native Date-Picker feuern oft kein blur, daher reicht der bisherige
  // Re-Render-on-blur nicht.
  Configurator.prototype._applyKindergartenOrSchoolDefault = function () {
    if (this._kosManual) return;
    var suggested = NS.suggestKindergartenOrSchool(NS.calcAge(this.form.child.birthday));
    if (!suggested) return;
    this.form.child.kindergartenOrSchool = suggested;
    var wrap = this.root.querySelector('[data-bs-field="child.kindergartenOrSchool"]');
    if (!wrap) return;
    wrap.querySelectorAll(".bs-c__chip[data-value]").forEach(function (chip) {
      chip.classList.toggle("is-active", chip.getAttribute("data-value") === suggested);
    });
  };

  Configurator.prototype.renderCheckbox = function (field) {
    var self = this;
    var wrap = el(
      '<div class="bs-c__field bs-c__field--checkbox' +
        (field.highlight ? " bs-c__field--checkbox-hl" : "") +
        '"></div>'
    );
    var id = "bs-cb-" + field.path.replace(/\W/g, "-");
    var line = el('<label class="bs-c__checkbox" for="' + id + '"></label>');
    var input = el('<input type="checkbox" id="' + id + '" />');
    input.checked = !!getPath(this.form, field.path);
    input.addEventListener("change", function () {
      setPath(self.form, field.path, input.checked);
      // rerender: bei Feldern, die abhaengige showIf-Felder ein-/ausblenden
      // (z. B. Geheimzutat-Toggle, der die Besonderheiten-Felder steuert).
      if (field.rerender) {
        self.stepError = "";
        self.render();
      }
    });
    line.appendChild(input);
    line.appendChild(el("<span>" + esc(field.label) + "</span>"));
    // Info-Popover (gleiche Optik wie _labelEl) neben der Checkbox-Zeile, falls
    // gesetzt. Bewusst als Sibling der <label>-Zeile, damit der Klick auf das
    // Info-Icon nicht ueber das umschliessende <label> die Checkbox togglet.
    if (field.info) {
      var row = el('<div class="bs-c__label-row bs-c__label-row--checkbox"></div>');
      row.appendChild(line);
      row.appendChild(this._infoDetailsEl(field.info));
      wrap.appendChild(row);
    } else {
      wrap.appendChild(line);
    }
    if (field.hint) wrap.appendChild(el('<p class="bs-c__hint">' + esc(field.hint) + "</p>"));
    return wrap;
  };

  Configurator.prototype.renderAddonCard = function (field) {
    var self = this;
    var active = !!getPath(this.form, field.path);
    var wrap = el(
      '<div class="bs-c__field bs-c__addon-card' + (active ? " is-active" : "") + '"></div>'
    );
    var id = "bs-addon-" + field.path.replace(/\W/g, "-");
    var header = el('<div class="bs-c__addon-card-header"></div>');
    var row = el('<div class="bs-c__addon-card-row"></div>');
    var line = el('<label class="bs-c__addon-card-toggle" for="' + id + '"></label>');
    var input = el('<input type="checkbox" id="' + id + '" />');
    input.checked = active;
    input.addEventListener("change", function () {
      setPath(self.form, field.path, input.checked);
      if (!input.checked && field.addonId === "special_event") {
        setPath(self.form, "story.specialEvents", null);
        if (self.bookType !== "wm") {
          setPath(self.form, "story.specialEventClub", null);
        }
      }
      self.stepError = "";
      if (field.rerender) self.render();
    });
    line.appendChild(input);
    line.appendChild(el('<span class="bs-c__addon-card-title">' + esc(this._addonCardTitle(field)) + "</span>"));
    row.appendChild(line);
    row.appendChild(
      el(
        '<span class="bs-c__addon-card-chevron" aria-hidden="true">' + (active ? "▾" : "▸") + "</span>"
      )
    );
    header.appendChild(row);
    if (field.info) {
      header.appendChild(
        this._infoDetailsEl(field.info, {
          triggerLabel: field.infoLabel || "Mehr erfahren",
          title: field.infoTitle,
        })
      );
    }
    if (field.hint) header.appendChild(el('<p class="bs-c__hint">' + esc(field.hint) + "</p>"));
    wrap.appendChild(header);

    if (active && field.children && field.children.length) {
      var body = el('<div class="bs-c__addon-card-body"></div>');
      field.children.forEach(function (child) {
        if (child.showIf && !child.showIf(self.form)) return;
        body.appendChild(self.renderField(child));
      });
      wrap.appendChild(body);
    }
    return wrap;
  };

  // ---- City Autocomplete (Open-Meteo, wie Create.vue searchCity) ------------

  // Label "Name, Region, Land" aus einem Open-Meteo-Ergebnis.
  Configurator.prototype._cityLabel = function (r) {
    return [r.name, r.admin1, r.country_code].filter(Boolean).join(", ");
  };

  // Kanonische cityDetails-Form (inkl. admin2-4/country_id/elevation) — identisch
  // für manuelle Suche UND Standort-Übernahme, damit Firebase-IDs/Struktur passen.
  Configurator.prototype._cityDetailsFromResult = function (r, label) {
    return {
      displayName: label,
      id: r.id || r.name,
      name: r.name || "",
      admin1: r.admin1 || "",
      admin1_id: r.admin1_id != null ? r.admin1_id : null,
      admin2: r.admin2 || null,
      admin2_id: r.admin2_id != null ? r.admin2_id : null,
      admin3: r.admin3 || null,
      admin3_id: r.admin3_id != null ? r.admin3_id : null,
      admin4: r.admin4 || null,
      admin4_id: r.admin4_id != null ? r.admin4_id : null,
      country: r.country || "",
      country_code: r.country_code || "",
      country_id: r.country_id != null ? r.country_id : null,
      latitude: r.latitude != null ? r.latitude : null,
      longitude: r.longitude != null ? r.longitude : null,
      elevation: r.elevation != null ? r.elevation : null,
      timezone: r.timezone || "",
      population: r.population != null ? r.population : null,
      feature_code: r.feature_code || "",
    };
  };

  // Übernimmt ein Open-Meteo-Ergebnis in die Form (city + cityDetails).
  Configurator.prototype._selectCityResult = function (r) {
    var label = this._cityLabel(r);
    this.form.child.city = label;
    this.form.child.cityDetails = this._cityDetailsFromResult(r, label);
    this.form.child.cityManual = false;
    this.form.child.cityManualText = "";
    this._cityManualOpen = false;
    this._cityManualReported = false;
    this._cityManualReportedText = "";
  };

  // Standort-Übernahme: Geolocation → Reverse-Geocode (BigDataCloud) → Stadtname
  // → Open-Meteo-Suche → nächstgelegener Treffer (gleiche Struktur wie manuell).
  Configurator.prototype.useMyLocation = function (btn, input, statusEl) {
    var self = this;
    if (!navigator.geolocation) {
      statusEl.textContent = "Standort wird von deinem Gerät nicht unterstützt.";
      return;
    }
    btn.disabled = true;
    statusEl.textContent = "📍 Standort wird ermittelt …";
    function dist(r, lat, lon) {
      var dlat = (r.latitude || 0) - lat, dlon = (r.longitude || 0) - lon;
      return dlat * dlat + dlon * dlon;
    }
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        var lat = pos.coords.latitude, lon = pos.coords.longitude;
        fetch(
          "https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=" +
            lat + "&longitude=" + lon + "&localityLanguage=de"
        )
          .then(function (r) { return r.json(); })
          .then(function (geo) {
            var cc = String(geo.countryCode || "").toUpperCase();
            if (["DE", "AT", "CH"].indexOf(cc) === -1) throw new Error("Standort liegt außerhalb von DE/AT/CH.");
            var cityName = geo.city || geo.locality || geo.principalSubdivision || "";
            if (!cityName) throw new Error("Keine Stadt am Standort erkannt.");
            return fetch(
              "https://geocoding-api.open-meteo.com/v1/search?name=" +
                encodeURIComponent(cityName) + "&count=10&language=de&format=json"
            )
              .then(function (r) { return r.json(); })
              .then(function (data) {
                var raw = Array.isArray(data && data.results) ? data.results : [];
                var cand = raw.filter(function (x) {
                  return ["DE", "AT", "CH"].indexOf(String(x.country_code || "").toUpperCase()) !== -1;
                });
                if (!cand.length) throw new Error("Stadt nicht gefunden: " + cityName);
                cand.sort(function (a, b) { return dist(a, lat, lon) - dist(b, lat, lon); });
                self._selectCityResult(cand[0]);
                input.value = self.form.child.city;
                self.stepError = "";
                self.render();
              });
          })
          .catch(function (e) {
            btn.disabled = false;
            statusEl.textContent = (e && e.message) || "Standort konnte nicht übernommen werden.";
          });
      },
      function (err) {
        btn.disabled = false;
        statusEl.textContent = err && err.code === 1 ? "Standortzugriff wurde abgelehnt." : "Standort aktuell nicht verfügbar.";
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  Configurator.prototype.renderCity = function (field) {
    var self = this;
    var manualActive = !!this.form.child.cityManual;
    var wrap = el('<div class="bs-c__field bs-c__field--city' + (manualActive ? " is-manual" : "") + '"></div>');
    wrap.appendChild(this._labelEl(field));
    var inputRow = el('<div class="bs-c__city-row' + (manualActive ? " is-disabled" : "") + '"></div>');
    var input = el('<input type="text" class="bs-c__input" autocomplete="off" placeholder="Stadt eingeben …" />');
    input.value = manualActive ? this.form.child.cityManualText || this.form.child.city || "" : this.form.child.city || "";
    if (manualActive) {
      input.disabled = true;
      input.classList.add("bs-c__input--disabled");
    }
    var list = el('<div class="bs-c__suggestions"></div>');
    var geoStatus = el('<p class="bs-c__hint bs-c__geo-status"></p>');

    input.addEventListener("input", function () {
      if (self.form.child.cityManual) return;
      self.form.child.city = input.value;
      self.form.child.cityDetails = null; // Auswahl invalidieren bis erneut gewählt
      clearTimeout(self._cityTimer);
      var q = input.value;
      self._cityTimer = setTimeout(function () {
        self.searchCity(q, list, input);
      }, 300);
    });
    inputRow.appendChild(input);
    // Lösch-Button (×): leert Eingabe + Auswahl, falls die Stadt nicht stimmt.
    if (this.form.child.city && !manualActive) {
      var clearBtn = el(
        '<button type="button" class="bs-c__city-clear" aria-label="Eingabe löschen" title="Eingabe löschen">×</button>'
      );
      clearBtn.addEventListener("click", function () {
        self.form.child.city = "";
        self.form.child.cityDetails = null;
        list.innerHTML = "";
        self.stepError = "";
        self.render();
      });
      inputRow.appendChild(clearBtn);
    }
    // Standort-Button (mobil besonders nützlich): übernimmt den aktuellen Standort.
    var geoBtn = el(
      '<button type="button" class="bs-c__geo" aria-label="Mein Standort übernehmen" title="Mein Standort übernehmen"><span aria-hidden="true">📍</span></button>'
    );
    if (manualActive) geoBtn.disabled = true;
    geoBtn.addEventListener("click", function () {
      if (self.form.child.cityManual) return;
      self.useMyLocation(geoBtn, input, geoStatus);
    });
    inputRow.appendChild(geoBtn);
    wrap.appendChild(inputRow);
    wrap.appendChild(list);
    wrap.appendChild(geoStatus);
    if (this.form.child.cityDetails && !manualActive) {
      wrap.appendChild(el('<p class="bs-c__hint">✓ Ausgewählt: ' + esc(this.form.child.city) + "</p>"));
    }

    if (!(this._cityManualOpen || manualActive)) {
      var missingLink = el('<button type="button" class="bs-c__city-missing">Deine Stadt ist nicht dabei?</button>');
      missingLink.addEventListener("click", function () {
        self._cityManualOpen = true;
        self.stepError = "";
        self.render();
      });
      wrap.appendChild(missingLink);
    } else {
      var box = el('<div class="bs-c__city-manual"></div>');
      var boxHead = el('<div class="bs-c__city-manual-head"></div>');
      boxHead.appendChild(
        el('<label class="bs-c__label">Gib deine Stadt manuell ein <span class="bs-c__req">*</span></label>')
      );
      var boxClose = el(
        '<button type="button" class="bs-c__city-manual-close" aria-label="Manuelle Eingabe schließen" title="Schließen">×</button>'
      );
      boxClose.addEventListener("click", function () {
        self._cityManualOpen = false;
        self.form.child.cityManual = false;
        self.form.child.cityManualText = "";
        if (!self.form.child.cityDetails) self.form.child.city = "";
        self._cityManualReported = false;
        self._cityManualReportedText = "";
        self.stepError = "";
        self.render();
      });
      boxHead.appendChild(boxClose);
      box.appendChild(boxHead);
      var manual = el('<input type="text" class="bs-c__input" autocomplete="off" placeholder="z. B. Musterdorf" />');
      manual.value = this.form.child.cityManualText || "";
      manual.addEventListener("input", function () {
        var text = manual.value;
        self.form.child.cityManualText = text;
        if (String(text).trim()) {
          self.form.child.cityManual = true;
          self.form.child.city = String(text).trim();
          self.form.child.cityDetails = null;
        } else {
          self.form.child.cityManual = false;
          self.form.child.city = "";
          self.form.child.cityDetails = null;
          self._cityManualReported = false;
          self._cityManualReportedText = "";
        }
        self.stepError = "";
        wrap.classList.toggle("is-manual", !!self.form.child.cityManual);
        var cityInput = wrap.querySelector(".bs-c__city-row .bs-c__input");
        var geo = wrap.querySelector(".bs-c__geo");
        var row = wrap.querySelector(".bs-c__city-row");
        var suggestions = wrap.querySelector(".bs-c__suggestions");
        if (suggestions && self.form.child.cityManual) suggestions.innerHTML = "";
        if (cityInput) {
          cityInput.disabled = !!self.form.child.cityManual;
          cityInput.classList.toggle("bs-c__input--disabled", !!self.form.child.cityManual);
          cityInput.value = self.form.child.city || "";
        }
        if (row) row.classList.toggle("is-disabled", !!self.form.child.cityManual);
        if (geo) geo.disabled = !!self.form.child.cityManual;
      });
      box.appendChild(manual);
      wrap.appendChild(box);
    }
    return wrap;
  };

  Configurator.prototype.searchCity = function (query, listEl, input) {
    var self = this;
    if (!query || query.length < 2) {
      listEl.innerHTML = "";
      return;
    }
    var allowed = ["DE", "AT", "CH"];
    fetch(
      "https://geocoding-api.open-meteo.com/v1/search?name=" +
        encodeURIComponent(query) +
        "&count=10&language=de&format=json"
    )
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var raw = Array.isArray(data && data.results) ? data.results : [];
        var nq = String(query).trim().toLowerCase();
        var results = raw
          .filter(function (r) {
            return allowed.indexOf(String(r.country_code || "").toUpperCase()) !== -1;
          })
          .filter(function (r) {
            if (nq.length >= 4) return true;
            return String(r.name || "").toLowerCase().indexOf(nq) === 0;
          })
          .slice(0, 8);
        listEl.innerHTML = "";
        if (!results.length) {
          listEl.appendChild(el('<div class="bs-c__suggestion is-empty">Keine passende Stadt gefunden.</div>'));
          // Query length only — the typed city itself is never reported.
          self._emit("city_no_result", { field: "child.city", query_len: String(query).trim().length });
          return;
        }
        results.forEach(function (r) {
          var label = self._cityLabel(r);
          var item = el('<div class="bs-c__suggestion">' + esc(label) + "</div>");
          item.addEventListener("click", function () {
            // Geteilter Pfad mit der Standort-Übernahme → identische cityDetails-Struktur.
            self._selectCityResult(r);
            input.value = self.form.child.city;
            listEl.innerHTML = "";
            self.stepError = "";
            self.render();
          });
          listEl.appendChild(item);
        });
      })
      .catch(function () {
        listEl.innerHTML = "";
        listEl.appendChild(el('<div class="bs-c__suggestion is-empty">Stadtsuche derzeit nicht verfügbar.</div>'));
        self._emit("city_search_error", { field: "child.city" });
      });
  };

  Configurator.prototype._reportCityManual = function () {
    var self = this;
    if (!this.form.child.cityManual) return;
    var cityText = String(this.form.child.city || "").trim();
    if (!cityText) return;
    if (this._cityManualReported && this._cityManualReportedText === cityText) return;
    this._cityManualReported = true;
    this._cityManualReportedText = cityText;
    if (!this.fnBase) return;
    fetch(this.fnBase + "/submitConfiguratorFeedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "city_manual",
        cityText: cityText.slice(0, 120),
        bookId: this.bookId || "",
        previewSessionId: this.previewSessionId || "",
      }),
    }).catch(function (e) {
      console.warn("[bs-configurator] Stadt-Meldung fehlgeschlagen:", e && e.message);
      self._cityManualReported = false;
    });
  };

  // ---- Repeatable Lists (trainer/teammate/rival/sibling) --------------------
  Configurator.prototype.renderList = function (field) {
    var self = this;
    var arr = getPath(this.form, field.path);
    if (!Array.isArray(arr)) {
      arr = [];
      setPath(this.form, field.path, arr);
    }
    // Pflicht-Listen (min>=1, z. B. Trainer/Mitspieler): immer mind. einen
    // Eintrag zeigen, damit der Nutzer den ohnehin verpflichtenden Erst-Eintrag
    // nicht erst per "+" anlegen muss. Leerer Seed-Eintrag zaehlt nicht als
    // ausgefuellt (validateStep filtert Leere) und wird im Payload verworfen
    // (cleanList/people droppen Leere) -> keine Parität-/Pflichtfeld-Nebenwirkung.
    if (arr.length === 0 && field.min >= 1) {
      arr.push(this._emptyListItem(field.item));
    }
    var wrap = el('<div class="bs-c__field bs-c__field--list"></div>');
    wrap.appendChild(this._labelEl(field));
    if (field.hint) wrap.appendChild(el('<p class="bs-c__hint">' + esc(field.hint) + "</p>"));

    arr.forEach(function (entry, idx) {
      wrap.appendChild(self.renderListItem(field, entry, idx, arr));
    });

    if (arr.length < field.max) {
      var add = el('<button type="button" class="bs-c__add">+ ' + esc(field.addLabel || "Hinzufügen") + "</button>");
      add.addEventListener("click", function () {
        arr.push(self._emptyListItem(field.item));
        self.render();
      });
      wrap.appendChild(add);
    }
    return wrap;
  };

  Configurator.prototype._emptyListItem = function (kind) {
    if (kind === "trainer") return { name: "", gender: "", parentRole: null };
    if (kind === "teammate") return { name: "", gender: "" };
    if (kind === "sibling") return { name: "", age: "", gender: "" };
    if (kind === "rival") return ""; // plain string
    return {};
  };

  Configurator.prototype.renderListItem = function (field, entry, idx, arr) {
    var self = this;
    var row = el('<div class="bs-c__list-item"></div>');
    var kind = field.item;

    if (kind === "rival") {
      var rInput = el('<input type="text" class="bs-c__input" placeholder="Gegner / Verein" />');
      rInput.value = entry || "";
      rInput.addEventListener("input", function () {
        arr[idx] = rInput.value;
      });
      row.appendChild(rInput);
    } else {
      var nameInput = el('<input type="text" class="bs-c__input" placeholder="Name" />');
      nameInput.value = entry.name || "";
      nameInput.addEventListener("input", function () {
        entry.name = nameInput.value;
      });
      row.appendChild(nameInput);

      if (kind === "sibling") {
        var ageInput = el('<input type="text" class="bs-c__input bs-c__input--age" inputmode="numeric" placeholder="Alter" />');
        ageInput.value = entry.age || "";
        ageInput.addEventListener("input", function () {
          entry.age = ageInput.value;
        });
        row.appendChild(ageInput);
      }

      if (kind === "trainer" || kind === "teammate" || kind === "sibling") {
        var gWrap = el('<div class="bs-c__chips bs-c__chips--mini"></div>');
        // Klar erkennbare Geschlechts-Icons statt ♂/♀: Kinder (Mitspieler/
        // Geschwister) als 👦/👧, Erwachsene (Trainer) als 👨/👩 + Wort-Label.
        var genderOpts =
          kind === "trainer"
            ? [
                { value: "male", emoji: "👨", label: "Mann" },
                { value: "female", emoji: "👩", label: "Frau" },
              ]
            : [
                { value: "male", emoji: "👦", label: "Junge" },
                { value: "female", emoji: "👧", label: "Mädchen" },
              ];
        genderOpts.forEach(function (g) {
          var chip = el(
            '<button type="button" class="bs-c__chip' + (entry.gender === g.value ? " is-active" : "") + '">' +
              '<span class="bs-c__chip-emoji" aria-hidden="true">' + g.emoji + "</span>" +
              esc(g.label) +
              "</button>"
          );
          chip.addEventListener("click", function () {
            entry.gender = g.value;
            if (kind === "trainer" && entry.parentRole) {
              entry.parentRole = g.value === "female" ? "mama" : "papa";
            }
            self.render();
          });
          gWrap.appendChild(chip);
        });
        row.appendChild(gWrap);
      }

      if (kind === "trainer") {
        // Prominente, eindeutige Box statt unscheinbarer Checkbox (Doppelrolle
        // Trainer+Elternteil führte regelmäßig zu Missverständnissen). Mit
        // Live-Vorschau, wie es im Buch erscheint, und Hinweis falls Geschlecht fehlt.
        var childName = (self.form.child.name || "").trim();
        var parentWord = entry.gender === "female" ? "Mama" : (entry.gender === "male" ? "Papa" : "Mama/Papa");
        var pbox = el('<div class="bs-c__parentbox' + (entry.parentRole ? " is-on" : "") + '"></div>');
        var pToggle = el(
          '<label class="bs-c__parentbox-toggle"><input type="checkbox"' +
            (entry.parentRole ? " checked" : "") +
            " /> <span class=\"bs-c__parentbox-txt\"><strong>👨‍👧 Ist dieser Trainer gleichzeitig ein Elternteil?</strong>" +
            "<br>Nur aktivieren, wenn <u>dieselbe Person</u> Trainer <u>und</u> Mama/Papa von " +
            (childName ? esc(childName) : "deinem Kind") +
            " ist.</span></label>"
        );
        pToggle.querySelector("input").addEventListener("change", function (e) {
          // Nur EIN Trainer darf parentRole haben (wie Vue toggleTrainerParent)
          arr.forEach(function (t, i) {
            t.parentRole = i === idx && e.target.checked ? (t.gender === "female" ? "mama" : "papa") : null;
          });
          self.render();
        });
        pbox.appendChild(pToggle);
        if (entry.parentRole) {
          if (!entry.gender) {
            pbox.appendChild(
              el('<p class="bs-c__parentbox-hint">⚠️ Bitte oben noch Mann/Frau wählen – sonst können wir Papa oder Mama nicht richtig zuordnen.</p>')
            );
          } else {
            pbox.appendChild(
              el(
                '<p class="bs-c__parentbox-result">✓ So steht es im Buch: <strong>' +
                  (esc(entry.name) || "dieser Trainer") +
                  " – " +
                  parentWord +
                  (childName ? " von " + esc(childName) : "") +
                  "</strong></p>"
              )
            );
          }
        }
        row.appendChild(pbox);
      }
    }

    if (arr.length > Math.max(field.min, 1) || (field.min === 0 && arr.length > 0)) {
      var rm = el('<button type="button" class="bs-c__remove" aria-label="Entfernen">×</button>');
      rm.addEventListener("click", function () {
        arr.splice(idx, 1);
        self.render();
      });
      row.appendChild(rm);
    }
    return row;
  };

  // ---- KI-Preview -----------------------------------------------------------
  Configurator.prototype.canGeneratePreview = function () {
    var c = this.form.child;
    return !!(
      c.name &&
      c.birthday &&
      c.gender &&
      c.hairColor &&
      c.hairLength &&
      c.hairType &&
      c.eyeColor &&
      c.skinColor &&
      c.faceFeatures
    );
  };

  // Signatur der generierungsrelevanten Aussehen-Felder. Aendert sich eines davon
  // nach einer Generierung, weicht die Signatur ab → Vorschau ist veraltet (stale).
  Configurator.prototype._previewSignature = function () {
    var c = this.form.child;
    return [
      c.gender,
      c.hairColor,
      c.hairLength,
      c.hairType,
      c.eyeColor,
      c.skinColor,
      c.faceFeatures,
      c.wearsGlasses ? "1" : "0",
      ageGroup(c.birthday),
    ].join("|");
  };

  // Vorschau ist veraltet: es existiert bereits ein Bild, gerade laeuft keine
  // Generierung und die aktuellen Aussehen-Felder weichen von der zuletzt
  // generierten Signatur ab.
  Configurator.prototype._previewStale = function () {
    return !!(
      this.form.child.previewImageUrl &&
      !this.previewLoading &&
      this.previewGeneratedSignature !== null &&
      this.previewGeneratedSignature !== this._previewSignature()
    );
  };

  Configurator.prototype.renderPreview = function (field) {
    var self = this;
    var c = this.form.child;
    // teaser: nur das angedeutete Buch (Titel + Silhouette/Logo) ohne
    // Generieren-Button/Status — fuer Step 1 ("Vorschau bereits auf Seite 1").
    var teaser = !!(field && field.teaser);
    var wrap = el('<div class="bs-c__field bs-c__preview"></div>');

    // Buch-Optik (portiert aus Vue WmCreatePreviewAside.vue): Cover-Rahmen mit
    // Titel-Overlay ("Maxis ballstories"), Bildflaeche und Logo-Wasserzeichen
    // unten rechts. Vor der echten Vorschau dient eine schwarze Silhouette
    // (geschlechtsabhaengig) als Platzhalter, damit es sofort wie ein Buch wirkt.
    var book = el('<div class="bs-c__book"></div>');
    // data-bs-book-title: erlaubt das Live-Update des Titels beim Tippen des
    // Vornamens (siehe renderText/syncBookTitle), ohne ein Full-Re-Render.
    book.appendChild(
      el('<div class="bs-c__book-title" data-bs-book-title>' + esc(coverLine1(c.name)) + "</div>")
    );
    var media = el('<div class="bs-c__book-media"></div>');
    if (c.previewImageUrl) {
      media.appendChild(el('<img class="bs-c__book-img" alt="Vorschau" src="' + esc(c.previewImageUrl) + '" />'));
    } else {
      var silUrl = c.gender === "female" ? this.silhouetteGirlUrl : this.silhouetteBoyUrl;
      if (silUrl) {
        media.appendChild(el('<img class="bs-c__book-silhouette" alt="" src="' + esc(silUrl) + '" />'));
      } else {
        media.appendChild(el('<div class="bs-c__preview-placeholder">Noch keine Vorschau</div>'));
      }
    }
    book.appendChild(media);
    if (this.logoUrl) {
      book.appendChild(el('<img class="bs-c__book-logo" alt="ballstories" src="' + esc(this.logoUrl) + '" />'));
    }
    // Vereinslogo auf dem Cover (wie auf der Club-Landingpage), oben links als
    // dezentes Badge — nur wenn der Konfigurator aus einer Club-Landing kam.
    if (this.form._club && this.form._club.logoUrl) {
      book.appendChild(
        el(
          '<img class="bs-c__book-club-logo" alt="Vereinslogo" src="' +
            esc(this.form._club.logoUrl) +
            '" />'
        )
      );
    }
    // WM-Cover-Badge: Länderflagge unten links (nur WM-Buch + Land gewählt),
    // analog zur Vue-App (.cover-flag). Backend erzeugt das gedruckte Badge aus
    // story.specialEventClub; hier nur die On-Screen-Vorschau.
    if (this.bookType === "wm" && this.form.story.specialEventClub) {
      var wcFlag = el(
        '<span class="bs-c__book-flag" role="img" aria-label="' +
          esc(NS.wmNationLabel(this.form.story.specialEventClub)) +
          '"></span>'
      );
      wcFlag.style.backgroundImage = "url(" + flagSvgUrl(this.form.story.specialEventClub) + ")";
      book.appendChild(wcFlag);
    }
    wrap.appendChild(book);

    if (!teaser && this.previewFromCache !== null && c.previewImageUrl) {
      var badge = el(
        '<span class="bs-c__book-cache" aria-hidden="true" title="' +
          (this.previewFromCache ? "Cache" : "Neu generiert") +
          '">' +
          (this.previewFromCache ? "↺" : "") +
          "</span>"
      );
      if (!this.previewFromCache) badge.classList.add("is-fresh");
      book.appendChild(badge);
    }

    // Teaser-Modus: Buch ohne Steuerelemente. Die echte KI-Vorschau (Button,
    // Status, Autoload) lebt weiterhin nur im Aussehen-Step.
    if (teaser) {
      return wrap;
    }

    // Lade-Overlay: waehrend der Generierung liegt der Lade-Zustand (Spinner +
    // Text) UEBER dem Buchbild — gleiche Optik wie das Stale-Overlay, damit der
    // Wechsel "neu erstellen" → "generiere" am selben Ort passiert (nicht der
    // Button unten). Liegt ueber Silhouette (1. Lauf) bzw. altem Bild (Re-Gen).
    if (this.previewLoading) {
      var load = el('<div class="bs-c__book-stale bs-c__book-loading"></div>');
      load.appendChild(el('<span class="bs-c__spinner" aria-hidden="true"></span>'));
      load.appendChild(el('<p class="bs-c__book-stale-hint">Generiere Vorschau …</p>'));
      book.appendChild(load);
    }

    // Stale-Overlay: Aussehen wurde nach der Generierung geaendert → die Vorschau
    // wird NICHT automatisch neu generiert (kein Trigger-Spam). Stattdessen ein
    // leicht grauer Overlay UEBER dem Buchbild mit einem Neu-generieren-Button,
    // damit klar wird "ich muss neu laden" (statt es wie ein Bug aussehen zu lassen).
    if (this._previewStale()) {
      var stale = el('<div class="bs-c__book-stale"></div>');
      stale.appendChild(
        el('<p class="bs-c__book-stale-hint">Aussehen geändert</p>')
      );
      var staleBtn = el(
        '<button type="button" class="bs-c__btn bs-c__btn--secondary bs-c__book-stale-btn">Vorschau neu erstellen</button>'
      );
      staleBtn.addEventListener("click", function () {
        self.generatePreview();
      });
      stale.appendChild(staleBtn);
      book.appendChild(stale);
    }

    var status = el('<p class="bs-c__preview-status">' + esc(this.previewError || "") + "</p>");
    wrap.appendChild(status);

    if (c.previewImageUrl && !this.previewLoading) {
      var hintLink = el('<button type="button" class="bs-c__textlink">Hinweis zur Vorschau</button>');
      hintLink.addEventListener("click", function () {
        self._previewHintOpen = !self._previewHintOpen;
        if (!self._previewHintOpen) self._previewFeedbackOpen = false;
        self.render();
      });
      wrap.appendChild(hintLink);
      if (this._previewHintOpen) {
        var hintBox = el('<div class="bs-c__preview-hint"></div>');
        hintBox.appendChild(
          el(
            "<p>So sieht dein Kind <strong>im Buch</strong> aus: als Illustration, die ihm möglichst nahekommt – kein fotografisches 1:1-Abbild.</p>"
          )
        );
        hintBox.appendChild(el("<p><strong>Passt etwas nicht?</strong> Sag uns kurz Bescheid – wir schauen uns das an.</p>"));
        if (this._feedbackSent.preview_mismatch) {
          hintBox.appendChild(el('<p class="bs-c__feedback-thanks">Danke – wir melden uns!</p>'));
        } else {
          var mismatch = el('<button type="button" class="bs-c__textlink">Passt nicht ganz</button>');
          mismatch.addEventListener("click", function () {
            self._openFeedback("preview_mismatch");
          });
          hintBox.appendChild(mismatch);
        }
        wrap.appendChild(hintBox);
      }
    }

    // Waehrend der Generierung liegt der Lade-Zustand bereits als Overlay auf dem
    // Bild (siehe oben) — der untere Button entfaellt dann, um Doppelung/Verwirrung
    // zu vermeiden. Bei vorhandenem Bild lebt "neu erstellen" als Stale-Overlay auf
    // dem Bild; der untere Button erscheint nur fuer die ALLERERSTE Generierung.
    if (!this.previewLoading && !c.previewImageUrl) {
      var btn = el(
        '<button type="button" class="bs-c__btn bs-c__btn--secondary">Vorschau erstellen</button>'
      );
      if (!this.canGeneratePreview()) btn.disabled = true;
      btn.addEventListener("click", function () {
        self.generatePreview();
      });
      wrap.appendChild(btn);
    }

    if (!this.canGeneratePreview()) {
      wrap.appendChild(
        el('<p class="bs-c__hint">Fülle Name, Geburtstag, Geschlecht und alle Aussehen-Felder aus, um die Vorschau zu erzeugen.</p>')
      );
    }

    // Autoload: sobald alle Pflicht-Felder fuer die Vorschau ausgefuellt sind,
    // einmalig automatisch generieren (kein Button-Klick noetig). Spaeteres
    // Re-Generieren laeuft ausschliesslich manuell ueber den Button, damit nicht
    // bei jeder Feldaenderung erneut die Firebase-Function getriggert wird.
    if (
      this.canGeneratePreview() &&
      !c.previewImageUrl &&
      !this.previewLoading &&
      !this.previewError &&
      !this.previewAutoTriggered
    ) {
      this.previewAutoTriggered = true;
      setTimeout(function () {
        self.generatePreview();
      }, 0);
    }
    return wrap;
  };

  Configurator.prototype.generatePreview = function () {
    var self = this;
    var c = this.form.child;
    this.previewLoading = true;
    this.previewError = "";
    this.previewFromCache = null;
    this._previewStartedAt = Date.now();
    this._emit("preview_start");
    this.render();

    fetch(this.fnBase + "/generatePreviewImage", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        skinTone: c.skinColor,
        facialFeatures: c.faceFeatures,
        hairColor: c.hairColor,
        hairLength: mapHairLength(c.hairLength),
        hairType: c.hairType,
        eyeColor: c.eyeColor,
        gender: c.gender,
        age: ageGroup(c.birthday),
        wearsGlasses: !!c.wearsGlasses,
        referenceImageId: 1,
        // Server-Cache aktiv (wie Vue): Backend liefert bei Treffer fromCache=true
        // aus child-previews-temp/cache/. Effektiv nur, wenn backend ENABLE_IMAGE_CACHE
        // ebenfalls aktiv ist.
        useCache: true,
      }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.error || "HTTP " + res.status);
          return data;
        });
      })
      .then(function (data) {
        // Sofortige Live-Vorschau aus base64 (clientseitig, wird NICHT persistiert).
        self.form.child.previewImageUrl = "data:image/png;base64," + data.image;
        // Signatur der jetzt generierten Aussehen-Felder festhalten → spaetere
        // Aenderungen markieren die Vorschau als veraltet (Stale-Overlay).
        self.previewGeneratedSignature = self._previewSignature();
        self.previewFromCache = !!data.fromCache;
        self.previewError = "";
        self._trackMetaPreviewSeen(!!data.fromCache);
        self._emit("preview_done", {
          ms: self._previewElapsed(),
          from_cache: data.fromCache ? "1" : "0",
        });
        self.render();
        // Gewatermarktes Cover (Preview + Logo unten rechts) fuer das
        // Warenkorb-Thumbnail erzeugen und separat hochladen — wie Vue
        // WatermarkedImage.vue. Komplett non-fatal: bei Fehler bleibt der
        // Warenkorb beim rohen Preview (siehe buildCartProps-Fallback).
        self
          .composePreviewCover(data.image)
          .then(function (coverB64) {
            return self
              .uploadPreview(coverB64, "child-previews-temp/" + self.previewSessionId + "-cover.png")
              .then(function (coverUrl) {
                self.form.child.previewCoverImageUrl = coverUrl;
              });
          })
          .catch(function (e) {
            console.warn("[bs-configurator] Cover-Composite fehlgeschlagen:", e.message);
          });
        // Wie Vue: base64 nach Firebase Storage hochladen und die absolute URL
        // persistieren. Bei Erfolg ersetzt die URL das base64 (buildPayload schreibt
        // nur echte URLs nach Firestore).
        return self
          .uploadPreview(data.image)
          .then(function (url) {
            self.form.child.previewImageUrl = url;
            self.previewLoading = false;
            self.render();
          })
          .catch(function (e) {
            // Upload-Fehler ist nicht fatal: Vorschau bleibt sichtbar (base64),
            // wird aber nicht persistiert. Buch wird trotzdem angelegt.
            console.warn("[bs-configurator] Preview-Upload fehlgeschlagen:", e.message);
            self.previewLoading = false;
            self.render();
          });
      })
      .catch(function (e) {
        self.previewLoading = false;
        self.previewError = "Fehler: " + e.message;
        self._emit("preview_error", { ms: self._previewElapsed() });
        self.render();
      });
  };

  // Milliseconds since the running preview request started (0 if none).
  Configurator.prototype._previewElapsed = function () {
    return this._previewStartedAt ? Date.now() - this._previewStartedAt : 0;
  };

  // Zeichnet das KI-Preview (base64) plus das ballstories-Logo unten rechts auf
  // ein Canvas und liefert das Composite als base64 (ohne data:-Prefix). Port von
  // football-storybooks/src/components/WatermarkedImage.vue (Logo = 8 % der kleineren
  // Kante, 5 % Padding). Quelle ist base64 (kein CORS) → Canvas wird nicht tainted;
  // das Logo ist eine gleich-origin Extension-Asset-URL.
  Configurator.prototype.composePreviewCover = function (base64) {
    var self = this;
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () {
        var canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || 1024;
        canvas.height = img.naturalHeight || 1024;
        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        var finish = function () {
          try {
            resolve(canvas.toDataURL("image/png").split(",")[1]);
          } catch (e) {
            reject(e);
          }
        };
        if (!self.logoUrl) return finish();
        var logo = new Image();
        logo.onload = function () {
          var minDim = Math.min(canvas.width, canvas.height);
          var logoH = minDim * 0.08;
          var ar = logo.naturalWidth && logo.naturalHeight ? logo.naturalWidth / logo.naturalHeight : 4.34;
          var logoW = logoH * ar;
          var pad = minDim * 0.05;
          ctx.drawImage(logo, canvas.width - logoW - pad, canvas.height - logoH - pad, logoW, logoH);
          finish();
        };
        logo.onerror = function () {
          finish();
        };
        logo.src = self.logoUrl;
      };
      img.onerror = function () {
        reject(new Error("compose: preview load failed"));
      };
      img.src = "data:image/png;base64," + base64;
    });
  };

  // Wie Vue uploadPreviewImage (firebase.js): base64 → Blob → Firebase Storage
  // (child-previews-temp/<sessionId>.png) → absolute Download-URL.
  // Nutzt die Storage-REST-API statt des Firebase-SDK; der Pfad ist per
  // storage.rules oeffentlich beschreibbar (image/*, < 5 MiB).
  Configurator.prototype.uploadPreview = function (base64, objectPath, contentType) {
    var self = this;
    var mime = contentType || "image/png";
    var blob = base64ToBlob(base64, mime);
    objectPath = objectPath || "child-previews-temp/" + this.previewSessionId + ".png";
    var uploadUrl =
      "https://firebasestorage.googleapis.com/v0/b/" +
      this.storageBucket +
      "/o?uploadType=media&name=" +
      encodeURIComponent(objectPath);
    return fetch(uploadUrl, {
      method: "POST",
      headers: { "Content-Type": mime },
      body: blob,
    }).then(function (res) {
      return res.json().then(function (meta) {
        if (!res.ok) throw new Error((meta.error && meta.error.message) || "upload HTTP " + res.status);
        var token = meta.downloadTokens;
        return (
          "https://firebasestorage.googleapis.com/v0/b/" +
          self.storageBucket +
          "/o/" +
          encodeURIComponent(meta.name) +
          "?alt=media" +
          (token ? "&token=" + token : "")
        );
      });
    });
  };

  // ---------------------------------------------------------------------------
  // AKTIONEN / NAVIGATION
  // ---------------------------------------------------------------------------
  // Label-Lookup über ENUMS (value -> label) für die Review-Übersicht.
  function enumLabel(enumKey, value) {
    var list = (NS.ENUMS && NS.ENUMS[enumKey]) || [];
    for (var i = 0; i < list.length; i++) if (list[i].value === value) return list[i].label;
    return value == null || value === "" ? "" : String(value);
  }
  function deDateStr(s) {
    var m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? m[3] + "." + m[2] + "." + m[1] : (s || "");
  }

  // Abschluss-Review: alle Angaben gruppiert nach Schritt + "Ändern"-Sprung.
  Configurator.prototype.renderReview = function () {
    var self = this;
    var f = this.form;
    var wrap = el('<div class="bs-c__review"></div>');
    wrap.appendChild(
      el('<p class="bs-c__review-intro">Bitte prüfe noch einmal alle Angaben – wir schreiben das Buch genau so. Mit <strong>Ändern</strong> springst du zum jeweiligen Schritt zurück.</p>')
    );

    function fullName() { return [f.child.name, f.child.lastname].filter(Boolean).join(" "); }
    function trainersStr() {
      return (f.club.trainers || []).filter(function (t) { return t.name; }).map(function (t) {
        var role = t.parentRole === "mama" ? "Mama" : (t.parentRole === "papa" ? "Papa" : "");
        return t.name + (role ? " (" + role + " von " + (f.child.name || "Kind") + ")" : "");
      }).join(", ");
    }
    function peopleStr(arr) { return (arr || []).filter(function (p) { return p && p.name; }).map(function (p) { return p.name; }).join(", "); }
    function anecdoteStr() {
      var d = (f.addonDetails && f.addonDetails.personalAnecdote) || {};
      var lines = ["besonderheit1", "besonderheit2", "besonderheit3"].map(function (k) { return String(d[k] || "").trim(); }).filter(Boolean);
      return lines.join(" · ");
    }

    var groups = [
      { id: "child", title: "😊 Über das Kind", rows: [
        ["Sprache", enumLabel("languages", f.story.language)],
        ["Name", fullName()],
        ["Geburtstag", deDateStr(f.child.birthday)],
        ["Geschlecht", enumLabel("genders", f.child.gender)],
        ["Kindergarten/Schule", enumLabel("kindergartenOrSchool", f.child.kindergartenOrSchool)],
        ["Schreibstil", f.story.language === "de" ? enumLabel("storyStyles", f.story.storyStyle) : ""],
      ] },
      { id: "appearance", title: "✨ Aussehen", rows: [
        ["Haarfarbe", enumLabel("hairColors", f.child.hairColor)],
        ["Haarlänge", enumLabel("hairLengths", f.child.hairLength)],
        ["Haartyp", enumLabel("hairTypes", f.child.hairType)],
        ["Augenfarbe", enumLabel("eyeColors", f.child.eyeColor)],
        ["Hautton", enumLabel("skinColors", f.child.skinColor)],
        ["Gesichtszüge", enumLabel("faceFeatures", f.child.faceFeatures)],
        ["Brille", f.child.wearsGlasses ? "Ja" : ""],
      ] },
      { id: "club", title: "⚽ Fußball-Welt", rows: [
        ["Wohnort", f.child.city || ""],
        ["Verein", f.club.name],
        ["Mannschaft", f.club.team],
        ["Trainer", trainersStr()],
        ["Trikotnummer", f.club.jerseyNumber],
        ["Trikotfarbe", enumLabel("jerseyColors", f.club.jerseyColor)],
        ["Position", enumLabel("positions", f.club.position)],
        ["Mitspieler", peopleStr(f.club.teammates)],
        ["Rivalen", (f.club.rivals || []).filter(Boolean).join(", ")],
        ["Geschwister", (f.child.siblings || []).filter(function (s) { return s && s.name; }).map(function (s) { return s.name + (s.age ? " (" + s.age + ")" : ""); }).join(", ")],
      ] },
      { id: "story", title: "🎯 Events & Werte", rows: [
        ["WM-Lieblingsteam", self.bookType === "wm" ? NS.wmNationLabel(f.story.specialEventClub) : ""],
        ["Besonderes Event", f.story.specialEvents === "season_start" ? "Saisonauftakt" : (f.story.specialEvents ? enumLabel("specialEvents", f.story.specialEvents) : "Ohne")],
        ["Werte-Botschaft", f.story.learnValues ? enumLabel("learnValues", f.story.learnValues) : "Ohne"],
        ["Geheimzutat (+4,99 €)", NS.isPersonalAnecdoteActive(f) ? (anecdoteStr() || "Ja") : ""],
        ["Persönliche Widmung", NS.isDedicationActive(f) ? (String((f.addonDetails.dedication && f.addonDetails.dedication.text) || "").trim() || "Ja") : ""],
      ] },
    ];

    groups.forEach(function (grp) {
      var idx = -1;
      for (var i = 0; i < self.steps.length; i++) { if (self.steps[i].id === grp.id) { idx = i; break; } }
      var card = el('<div class="bs-c__review-card"></div>');
      var head = el('<div class="bs-c__review-head"></div>');
      head.appendChild(el('<h3 class="bs-c__review-title">' + esc(grp.title) + "</h3>"));
      if (idx >= 0) {
        var change = el('<button type="button" class="bs-c__review-edit">Ändern</button>');
        change.addEventListener("click", function () {
          self.stepError = "";
          self.stepIndex = idx;
          self.render();
          self._scrollTop();
          self._trackMetaStep();
        });
        head.appendChild(change);
      }
      card.appendChild(head);
      var list = el('<div class="bs-c__review-list"></div>');
      grp.rows.forEach(function (r) {
        if (r[1] == null || String(r[1]).trim() === "") return;
        list.appendChild(
          el('<div class="bs-c__review-row"><span class="bs-c__review-k">' + esc(r[0]) + '</span><span class="bs-c__review-v">' + esc(String(r[1])) + "</span></div>")
        );
      });
      card.appendChild(list);
      wrap.appendChild(card);
    });

    return wrap;
  };

  Configurator.prototype.renderActions = function (step) {
    var self = this;
    var box = el('<div class="bs-c__actions' + (step.isFinal ? "" : " bs-c__actions--step") + '"></div>');

    if (this.stepIndex > 0) {
      var back = el('<button type="button" class="bs-c__btn bs-c__btn--ghost">← Zurück</button>');
      back.addEventListener("click", function () {
        self.stepError = "";
        self.stepIndex--;
        self.render();
        self._scrollTop();
        self._trackMetaStep();
      });
      box.appendChild(back);
    }

    if (step.skippable) {
      var skip = el('<button type="button" class="bs-c__btn bs-c__btn--ghost">' + esc(step.skipLabel || "Überspringen →") + "</button>");
      skip.addEventListener("click", function () {
        self.next(true);
      });
      box.appendChild(skip);
    }

    if (step.isFinal) {
      if (this.editLineKey) {
        // Edit-Modus: dieselbe Cart-Zeile aktualisieren.
        var save = el('<button type="button" class="bs-c__btn bs-c__btn--primary">Änderungen speichern</button>');
        save.addEventListener("click", function () {
          self.submit(save);
        });
        box.appendChild(save);
      } else {
        // Sekundaer: nur in den Warenkorb (ohne Checkout-Sprung) → weiteres Buch erstellen.
        var addOnly = el('<button type="button" class="bs-c__btn bs-c__btn--ghost">Nur in den Warenkorb legen</button>');
        addOnly.addEventListener("click", function () {
          self.submitAddOnly(addOnly);
        });
        box.appendChild(addOnly);

        var cart = el('<button type="button" class="bs-c__btn bs-c__btn--primary">In den Warenkorb &amp; zum Checkout</button>');
        cart.addEventListener("click", function () {
          self.submit(cart);
        });
        box.appendChild(cart);
      }
    } else {
      var nextBtn = el('<button type="button" class="bs-c__btn bs-c__btn--primary">Weiter →</button>');
      nextBtn.addEventListener("click", function () {
        self.next(false);
      });
      box.appendChild(nextBtn);
    }
    return box;
  };

  Configurator.prototype._scrollTop = function () {
    try {
      this.root.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) {}
  };

  Configurator.prototype.next = function (skip) {
    if (!skip) {
      var err = this.validateStep(this.steps[this.stepIndex]);
      if (err) {
        this.stepError = err;
        this.render();
        return;
      }
      this._reportCityManual();
    }
    this.stepError = "";
    if (this.stepIndex < this.steps.length - 1) {
      this.stepIndex++;
      this.render();
      this._scrollTop();
      this._trackMetaStep();
    }
  };

  // ---- Validierung pro Schritt ----------------------------------------------
  Configurator.prototype._fail = function (field, msg) {
    // Merkt sich das fehlerhafte Feld (Pfad) fürs Inline-Highlight in renderField.
    this.errorFieldKey = field ? field.path : null;
    if (field) this._emit("field_error", { field: field.path, reason: this._failReason(field) });
    return msg;
  };

  // Classifies a validation failure without reading the message text, so the
  // reported reason survives wording changes. Returns a fixed set of codes and
  // never any customer input.
  Configurator.prototype._failReason = function (field) {
    if (field.path === "child.birthday") {
      var age = NS.calcAge(this.form.child.birthday);
      if (age != null && age < 5) return "age_under_5";
    }
    if (field.type === "city" && !this.form.child.cityManual && String(this.form.child.city || "").trim() && !this.form.child.cityDetails) {
      return "not_selected_from_list";
    }
    var val = getPath(this.form, field.path);
    if (Array.isArray(val)) return val.length ? "incomplete" : "empty";
    if (val === undefined || val === null || String(val).trim() === "") return "empty";
    return "invalid";
  };

  Configurator.prototype.validateStep = function (step) {
    var form = this.form;
    this.errorFieldKey = null;
    var fields = this.visibleFields(step);
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];

      // Listen
      if (f.type === "list") {
        var arr = getPath(form, f.path) || [];
        var filled = arr.filter(function (e) {
          return f.item === "rival" ? String(e || "").trim() : String((e && e.name) || "").trim();
        });
        if (f.min >= 1 && filled.length < 1) return this._fail(f, f.requiredMsg || "Bitte mindestens einen Eintrag ausfüllen.");
        // Cross-required: name↔gender bei trainer/teammate/sibling
        if (f.item === "trainer" || f.item === "teammate" || f.item === "sibling") {
          for (var j = 0; j < arr.length; j++) {
            var e = arr[j];
            var hasName = String(e.name || "").trim();
            var hasGender = String(e.gender || "").trim();
            var hasAge = f.item === "sibling" ? String(e.age || "").trim() : "";
            var any = hasName || hasGender || hasAge;
            if (any && (!hasName || !hasGender)) {
              return this._fail(f, "Bitte bei jedem Eintrag Name und Geschlecht angeben.");
            }
            // Geschwister-Alter ist Pflicht: fehlt es, entstand im Backend-Kontext
            // eine Scheinperson aus der leeren Altersangabe.
            if (f.item === "sibling" && any && !hasAge) {
              return this._fail(f, "Bitte bei jedem Geschwisterkind auch das Alter angeben.");
            }
          }
        }
        continue;
      }

      // City
      if (f.type === "city") {
        if (form.child.cityManual) {
          if (!String(form.child.city || "").trim()) return this._fail(f, "Bitte gib den Wohnort an.");
          continue;
        }
        if (f.required && !String(form.child.city || "").trim()) return this._fail(f, "Bitte gib den Wohnort an.");
        if (f.required && !form.child.cityDetails) return this._fail(f, "Bitte wähle eine Stadt aus der Liste.");
        continue;
      }

      // Custom validate
      if (f.validate) {
        var msg = f.validate(form);
        if (msg) return this._fail(f, msg);
      }

      // Addon card: validate nested fields when active
      if (f.type === "addonCard") {
        if (!getPath(form, f.path)) continue;
        var children = f.children || [];
        for (var c = 0; c < children.length; c++) {
          var child = children[c];
          if (child.showIf && !child.showIf(form)) continue;
          if (child.validate) {
            var childMsg = child.validate(form);
            if (childMsg) return this._fail(child, childMsg);
          }
          if (child.required) {
            var childVal = getPath(form, child.path);
            if (childVal === undefined || childVal === null || String(childVal).trim() === "") {
              return this._fail(child, "Bitte „" + child.label + "“ ausfüllen.");
            }
          }
        }
        continue;
      }

      // Required (chips/text/date)
      if (f.required) {
        var val = getPath(form, f.path);
        if (val === undefined || val === null || String(val).trim() === "") {
          return this._fail(f, "Bitte „" + f.label + "“ ausfüllen.");
        }
      }
    }
    this.errorFieldKey = null;
    return "";
  };

  // ---------------------------------------------------------------------------
  // PAYLOAD (faithful subset von book.js buildBookPayload)
  // ---------------------------------------------------------------------------
  Configurator.prototype.buildPayload = function () {
    var f = this.form;
    var age = NS.calcAge(f.child.birthday);

    function num(v) {
      if (v == null || v === "") return null;
      var n = Number(v);
      return isFinite(n) ? n : null;
    }
    function cleanList(a) {
      return (a || [])
        .map(function (x) {
          return String(x).trim();
        })
        .filter(Boolean);
    }
    function people(a) {
      return (a || [])
        .map(function (p) {
          return { name: String(p.name || "").trim(), gender: p.gender || "" };
        })
        .filter(function (p) {
          return p.name;
        })
        .slice(0, 5);
    }

    // Kanonische City-Form wie Vue (buildCityObject): vollstaendiges Objekt mit
    // allen Keys/Null-Defaults, damit das Firestore-Dokument 1:1 zur Vue-Form passt.
    var city = buildCityObject(f.child.cityDetails, f.child.city);

    var bookPayload = {
      customerId: null, // Option A: Draft ohne Kunde — Webhook backfillt später
      child: {
        name: cleanText(f.child.name),
        lastname: cleanText(f.child.lastname),
        birthday: f.child.birthday || "",
        age: age,
        gender: f.child.gender || "",
        kindergartenOrSchool: f.child.kindergartenOrSchool || null,
        appearance: {
          hairColor: f.child.hairColor || "",
          hairLength: f.child.hairLength || "",
          hairType: normalizeHairType(f.child.hairType),
          skinColor: f.child.skinColor || "",
          eyeColor: f.child.eyeColor || "",
          faceFeatures: f.child.faceFeatures || "",
          wearsGlasses: !!f.child.wearsGlasses,
          // Wie Vue: hier steht die absolute Firebase-Storage-URL des Previews
          // (von uploadPreview gesetzt). Niemals das rohe Base64 schreiben — es
          // sprengt Firestores 1-MiB-Feldlimit ("Property child contains an
          // invalid nested entity"). Falls der Upload fehlschlug und nur das
          // base64-Fallback (data:) vorliegt, wird null persistiert.
          previewImageUrl:
            f.child.previewImageUrl && f.child.previewImageUrl.indexOf("data:") !== 0
              ? f.child.previewImageUrl
              : null,
        },
        city: city,
        siblings: (f.child.siblings || [])
          .map(function (s) {
            return { name: String(s.name || "").trim(), age: String(s.age || "").trim(), gender: s.gender || "" };
          })
          // Nur benannte Geschwister übernehmen: ein Eintrag mit Alter, aber ohne
          // Namen erzeugte im Lektorat-Kontext eine erfundene Person.
          .filter(function (s) {
            return s.name;
          })
          .slice(0, 3),
      },
      club: {
        name: cleanText(f.club.name),
        team: (f.club.team || "").toUpperCase(),
        position: f.club.position || "",
        jerseyNumber: num(f.club.jerseyNumber),
        jerseyColor: f.club.jerseyColor || "",
        teammates: people(f.club.teammates),
        rivals: cleanList(f.club.rivals).slice(0, 3),
        trainers: (f.club.trainers || [])
          .map(function (t) {
            return {
              name: String(t.name || "").trim(),
              gender: t.gender || "",
              parentRole: t.parentRole === "mama" || t.parentRole === "papa" ? t.parentRole : null,
            };
          })
          .filter(function (t) {
            return t.name;
          }),
      },
      story: {
        language: f.story.language || "de",
        storyStyle: f.story.storyStyle || "classic",
        learnValues: f.story.learnValues || null,
        specialEvents: f.story.specialEvents || null,
        specialEventClub: f.story.specialEventClub || null,
        generatedText: null,
      },
      parentBookId: this.parentBookId || null,
      customerHighlights: null,
      clubChange: false,
      status: "draft_story",
      bookType: this.bookType,
      // Vereins-Landing (Analytics / Story-Kontext): nur gesetzt, wenn der
      // Konfigurator via `?club=<handle>` geoeffnet wurde (siehe _maybeApplyClub).
      // Parität zu Vue book.js (buildBookPayload): clubSlug nur mit fromClubLanding.
      fromClubLanding: !!(f._club && f._club.fromLanding),
      clubSlug: f._club && f._club.fromLanding && f._club.slug ? f._club.slug : null,
    };

    // Add-ons sammeln (faithful zu football-storybooks/src/lib/addons.js).
    var addons = [];
    var addonDetails = {};

    // WM-Buch: Das Backend (book-writer-mastra) erkennt die WM-Geschichte
    // AUSSCHLIESSLICH über story.specialEvents === 'world_cup_men' (nicht über
    // bookType). Wie in der Vue-App (book.js: specialEvents='world_cup_men' ->
    // Addon 'wm_special') muss der WM-Buchtyp dieses Event + Addon setzen, sonst
    // wird ein WM-Buch als normales Buch generiert. Preisneutral (eigener
    // WM-Produktpreis); das Addon dient nur der Erkennung/Konsistenz.
    if (this.bookType === "wm") {
      bookPayload.story.specialEvents = "world_cup_men";
      addons.push("wm_special");
    }

    // Saisonauftakt-Buch: eigener Buchtyp/Variante, analog WM. Das Backend erkennt
    // die Geschichte über story.specialEvents === 'season_start' (kein Addon nötig).
    if (this.bookType === "saisonauftakt") {
      bookPayload.story.specialEvents = "season_start";
    }

    // Erstleser-Buch (Leseanfänger): eigener Buchtyp/Variante. Das Backend erkennt
    // die vereinfachte Variante über story.readingMode === 'beginner'
    // (siehe READING-MODE-BEGINNER.md). Guards (storyStyle→classic etc.) macht das Backend.
    if (this.bookType === "erstleser") {
      bookPayload.story.readingMode = "beginner";
    }

    // Besonderes Event (special_event): kostenpflichtiges Add-on für Klassisch/Folgebuch.
    var se = (f.addonDetails && f.addonDetails.specialEvent) || {};
    if (this.bookType !== "wm" && this.bookType !== "saisonauftakt") {
      if (se.active && f.story.specialEvents) {
        addons.push("special_event");
        bookPayload.story.specialEvents = f.story.specialEvents;
        bookPayload.story.specialEventClub = f.story.specialEventClub || null;
      } else {
        bookPayload.story.specialEvents = null;
        bookPayload.story.specialEventClub = null;
      }
    }

    // Geheimzutat (personal_anecdote): bis zu 3 Freitexte. Aktiv nur, wenn die
    // Checkbox gesetzt ist UND mindestens ein Feld gefüllt ist. Schreibt zusätzlich
    // bookPayload.customerHighlights als Bullet-Liste (Backend liest dieses Feld,
    // wie Vue buildCustomerHighlightsFromPersonalAnecdote).
    var pa = (f.addonDetails && f.addonDetails.personalAnecdote) || {};
    if (pa.active) {
      var lines = ["besonderheit1", "besonderheit2", "besonderheit3"]
        .map(function (k) {
          return String(pa[k] || "").trim().slice(0, 120);
        })
        .filter(Boolean);
      if (lines.length) {
        addons.push("personal_anecdote");
        addonDetails.personal_anecdote = {
          besonderheit1: lines[0] || "",
          besonderheit2: lines[1] || "",
          besonderheit3: lines[2] || "",
        };
        bookPayload.customerHighlights = lines
          .map(function (l) {
            return "• " + l;
          })
          .join("\n");
      }
    }

    // Persönliche Widmung (dedication): eigenes Add-on/Produkt. Schreibt das
    // Top-Level-Feld bookPayload.dedication (Backend: bookCreationCore sanitized,
    // generatePdf rendert die Widmungsseite). Das Foto ist bereits als Firebase-
    // Storage-URL im Form-State (renderFile) – nie rohes Base64 hier hineinschreiben.
    var ded = (f.addonDetails && f.addonDetails.dedication) || {};
    if (ded.active) {
      var dedText = String(ded.text || "").trim().slice(0, 300);
      if (dedText) {
        addons.push("dedication");
        var dedImageUrl =
          typeof ded.imageUrl === "string" && /^https?:\/\//.test(ded.imageUrl)
            ? ded.imageUrl
            : undefined;
        bookPayload.dedication = {
          text: dedText,
          fromName: String(ded.fromName || "").trim().slice(0, 120) || undefined,
          imageUrl: dedImageUrl,
        };
        // Audit-Trail: nur wenn tatsächlich ein Foto dabei ist, die Rechte-/Datenschutz-
        // Bestätigung + Zeitpunkt mit an die Order hängen (Nachweis der Einwilligung).
        if (dedImageUrl) {
          bookPayload.dedication.photoConsent = {
            accepted: !!ded.photoConsent,
            acceptedAt: new Date().toISOString(),
          };
        }
      }
    }

    if (addons.length) bookPayload.addons = addons;
    if (Object.keys(addonDetails).length) bookPayload.addonDetails = addonDetails;

    // Vereins-Landing (Parität zu Vue book.js): Der promptDetails-Text der
    // Landingpage fliesst als customerHighlights ein — aber nur als Fallback,
    // falls die Geheimzutat (personal_anecdote) nicht schon customerHighlights
    // gesetzt hat (Anekdote gewinnt). Plus: Vereinslogo kostenlos aufs Cover.
    if (f._club && f._club.fromLanding) {
      if (!bookPayload.customerHighlights && f._club.promptDetails) {
        bookPayload.customerHighlights = f._club.promptDetails;
      }
      if (f._club.logoUrl) bookPayload.customLogoUrl = f._club.logoUrl;
    }

    // Referral + Newsletter-Consent kommen aus dem Shopify-Checkout und werden
    // vom orders/paid-Webhook nachgetragen (Option A). Im Draft daher leer.
    var customerPayload = {
      email: "",
      firstName: "",
      lastName: "",
      address: null,
      consent: false,
      newsletter: false,
      referralSource: null,
    };

    return { bookId: this.bookId, bookPayload: bookPayload, customerPayload: customerPayload };
  };

  // ---------------------------------------------------------------------------
  // SUBMIT: Cart-Add → Checkout
  //
  // Kein Pre-Cart-Write nach Firebase mehr: das vollstaendige Buch-Payload reist
  // als _bookPayload Line-Item-Property mit dem Auftrag bis zur bezahlten Order.
  // Der orders/paid-Webhook legt das Buch dann post-Order in Firestore an. So
  // entstehen keine Orphan-Drafts bei Checkout-Abbruch.
  // ---------------------------------------------------------------------------
  // SUBMIT: legt im Edit-Modus dieselbe Cart-Zeile neu an (update), sonst neue
  // Zeile → Checkout-Redirect.
  Configurator.prototype.submit = function (btn) {
    var self = this;
    var err = this.validateStep(this.steps[this.stepIndex]);
    if (err) {
      this.stepError = err;
      this.render();
      return;
    }
    this._reportCityManual();
    btn.disabled = true;
    this.stepError = "";

    var payload = this.buildPayload();

    if (this.editLineKey) {
      // Edit-Modus: bestehende Cart-Zeile in-place aktualisieren → zurueck zum Warenkorb.
      this._trackMetaComplete();
      this._emit("complete", { mode: "edit" });
      this.updateCartLine(payload, this.editLineKey)
        .then(function () {
          window.location.href = "/cart";
        })
        .catch(function (e) {
          self.stepError = "Fehler: " + e.message;
          btn.disabled = false;
          self._emit("cart_error", { mode: "edit" });
          self.render();
        });
      return;
    }

    // Native Cart-Add mit Line Item Properties (inkl. _bookPayload) → Checkout
    this._trackMetaComplete();
    this._emit("complete", { mode: "checkout" });
    this.addToCart(payload)
      .then(function () {
        window.location.href = self.cartRedirect;
      })
      .catch(function (e) {
        self.stepError = "Fehler: " + e.message;
        btn.disabled = false;
        self._emit("cart_error", { mode: "checkout" });
        self.render();
      });
  };

  // Sekundaer-Aktion: nur in den Warenkorb legen, KEIN Checkout-Sprung. Danach
  // Modal schliessen, auf der Ursprungsseite bleiben und einen kurzen Toast
  // zeigen. Das Formular wird zurueckgesetzt, damit ein erneutes Oeffnen frisch
  // startet (weiteres Buch ohne Ruecksprung).
  Configurator.prototype.submitAddOnly = function (btn) {
    var self = this;
    var err = this.validateStep(this.steps[this.stepIndex]);
    if (err) {
      this.stepError = err;
      this.render();
      return;
    }
    btn.disabled = true;
    this.stepError = "";

    var payload = this.buildPayload();
    this._trackMetaComplete();
    this._emit("complete", { mode: "add_only" });
    this.addToCart(payload)
      .then(function () {
        self._resetForNewBook();
        self.render();
        self.closeModal();
        self.showToast("✓ Buch liegt im Warenkorb!", "/cart", "Warenkorb ansehen →");
      })
      .catch(function (e) {
        self.stepError = "Fehler: " + e.message;
        btn.disabled = false;
        self._emit("cart_error", { mode: "add_only" });
        self.render();
      });
  };

  // Schliesst das umgebende Modal (falls im Modal-Modus). Nutzt den Close-Button
  // (von bs-modal.js verdrahtet) bzw. dialog.close() als Fallback.
  Configurator.prototype.closeModal = function () {
    var app = this.root.closest("[data-bs-app]");
    if (!app) return;
    var closeBtn = app.querySelector("[data-bs-close]");
    if (closeBtn) {
      closeBtn.click();
      return;
    }
    var dialog = app.querySelector("[data-bs-modal]");
    if (dialog && typeof dialog.close === "function" && dialog.open) dialog.close();
  };

  // Oeffnet das umgebende Modal (falls im Modal-Modus) ueber den Trigger-Button,
  // den bs-modal.js verdrahtet.
  Configurator.prototype.openModal = function () {
    var app = this.root.closest("[data-bs-app]");
    if (!app) return;
    var opener = app.querySelector("[data-bs-open]");
    if (opener) opener.click();
  };

  // Kurzer, selbst-entfernender Toast am unteren Bildschirmrand. Bewusst ohne
  // Theme-Abhaengigkeit: Klasse .bs-toast wird in bs-configurator.css gestylt.
  Configurator.prototype.showToast = function (message, actionHref, actionLabel) {
    // Doppelte Toasts vermeiden.
    var existing = document.querySelector(".bs-toast");
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var toast = document.createElement("div");
    toast.className = "bs-toast";
    toast.setAttribute("role", "status");
    var msg = document.createElement("span");
    msg.className = "bs-toast__msg";
    msg.textContent = message;
    toast.appendChild(msg);
    if (actionHref && actionLabel) {
      var a = document.createElement("a");
      a.className = "bs-toast__action";
      a.href = actionHref;
      a.textContent = actionLabel;
      toast.appendChild(a);
    }
    document.body.appendChild(toast);
    // Reflow erzwingen, dann sichtbar schalten (CSS-Transition).
    void toast.offsetWidth;
    toast.classList.add("bs-toast--visible");
    setTimeout(function () {
      toast.classList.remove("bs-toast--visible");
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 300);
    }, 5000);
  };

  // Baut die Line-Item-Properties (geteilt von add.js und change.js).
  //
  // _bookPayload traegt das vollstaendige Buch-Payload (JSON-String, ~2 KiB)
  // nativ am Auftrag bis zur bezahlten Order. Shopify Flow ("Order paid")
  // reicht diesen Rohwert post-Order an createBook durch (das den String selbst
  // parst). So entfaellt der Pre-Cart-Write nach Firebase und es entstehen keine
  // Orphan-Drafts bei Checkout-Abbruch. '_'-Prefix = im Checkout fuer Kunden
  // unsichtbar, aber auf der Order gespeichert.
  // Baut ein lesbares deutsches Recap aller Angaben (für die Bestellmail).
  // Wird als verstecktes Property `_bookSummary` mitgegeben (Cart bleibt sauber,
  // die Shopify-Bestellbestätigung liest es per line.properties._bookSummary aus).
  // Label-Maps spiegeln bs-configurator-schema.js.
  function buildBookSummary(bp) {
    if (!bp) return "";
    var M = {
      gender: { male: "Junge", female: "Mädchen" },
      kos: { kindergarten: "Kindergarten", school: "Schule" },
      hairColor: { blonde: "Blond", "dark-blonde": "Dunkelblond", brown: "Braun", black: "Schwarz", red: "Rot" },
      hairLength: { "very-short": "Ganz kurz", short: "Kurz", medium: "Mittel", long: "Lang", "very-long": "Sehr lang" },
      hairType: { straight: "Glatt", wavy: "Wellig", curly: "Lockig", coily: "Kraus", zopf: "Zopf" },
      skinColor: { "very light": "Sehr Hell", light: "Hell", medium: "Mittel", dark: "Dunkel", "very dark": "Sehr Dunkel" },
      eyeColor: { blue: "Blau", brown: "Braun", green: "Grün", gray: "Grau" },
      storyStyle: { classic: "Klassisch", adventurous: "Abenteuerlich", wild: "Wild & Frech" },
      learnValues: { courage: "Mut", fairplay: "Fairplay", friendship: "Freundschaft", team_spirit: "Teamgeist", handling_defeat: "Umgang mit Niederlagen" },
      specialEvents: { training_camp: "Mehrtägiges Trainingscamp", tryout_big_club: "Probetraining bei großem Verein", team_trip: "Ausflug mit der Mannschaft", tournament: "Großes Turnier", league_championship: "Saisonfinale in der Liga", world_cup_men: "WM Special", season_start: "Saisonauftakt" },
      position: { goalkeeper: "Torwart", defender: "Verteidiger", midfield: "Mittelfeld", striker: "Stürmer" },
      jerseyColor: { red: "Rot", blue: "Blau", green: "Grün", yellow: "Gelb", white: "Weiß", black: "Schwarz", gray: "Grau", purple: "Lila", orange: "Orange", pink: "Rosa" },
      language: { de: "Deutsch", en: "Englisch" },
    };
    function lbl(map, v) { return (v != null && map[v]) ? map[v] : (v || ""); }
    function deDate(s) { var m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? m[3] + "." + m[2] + "." + m[1] : (s || ""); }
    var lines = [];
    function add(label, val) { if (val != null && String(val).trim() !== "") lines.push(label + ": " + String(val).trim()); }

    var c = bp.child || {};
    var ap = c.appearance || {};
    var cl = bp.club || {};
    var st = bp.story || {};

    add("Name", [c.name, c.lastname].filter(Boolean).join(" "));
    add("Alter", c.age != null ? c.age + " Jahre" : "");
    add("Geschlecht", lbl(M.gender, c.gender));
    add("Geburtstag", deDate(c.birthday));
    add("Kindergarten/Schule", lbl(M.kos, c.kindergartenOrSchool));
    add("Wohnort", c.city ? (c.city.displayName || c.city.name || "") : "");

    var look = [
      lbl(M.hairColor, ap.hairColor),
      lbl(M.hairLength, ap.hairLength),
      lbl(M.hairType, ap.hairType),
    ].filter(Boolean).join(", ");
    add("Haare", look);
    add("Augenfarbe", lbl(M.eyeColor, ap.eyeColor));
    add("Hautton", lbl(M.skinColor, ap.skinColor));
    if (ap.wearsGlasses) add("Brille", "Ja");
    add("Gesichtszüge", ap.faceFeatures);

    if (Array.isArray(c.siblings) && c.siblings.length) {
      add("Geschwister", c.siblings.map(function (s) { return s.name + (s.age ? " (" + s.age + ")" : ""); }).join(", "));
    }

    add("Verein", cl.name);
    add("Mannschaft", cl.team);
    add("Position", lbl(M.position, cl.position));
    var trikot = [lbl(M.jerseyColor, cl.jerseyColor), cl.jerseyNumber != null ? "Nr. " + cl.jerseyNumber : ""].filter(Boolean).join(", ");
    add("Trikot", trikot);
    if (Array.isArray(cl.teammates) && cl.teammates.length) add("Mitspieler", cl.teammates.map(function (p) { return p.name; }).join(", "));
    if (Array.isArray(cl.rivals) && cl.rivals.length) add("Rivalen", cl.rivals.join(", "));
    if (Array.isArray(cl.trainers) && cl.trainers.length) {
      add("Trainer", cl.trainers.map(function (t) {
        var role = t.parentRole === "papa" ? "Papa" : (t.parentRole === "mama" ? "Mama" : "");
        // Eltern-Trainer DEUTLICH kennzeichnen (häufige Fehlerquelle).
        return t.name + (role ? " (ist " + role + " von " + (c.name || "dem Kind") + ")" : "");
      }).join(", "));
    }

    add("Sprache", lbl(M.language, st.language));
    add("Schreibstil", lbl(M.storyStyle, st.storyStyle));
    add("Werte", lbl(M.learnValues, st.learnValues));
    add("Besonderes Event", lbl(M.specialEvents, st.specialEvents));
    if (bp.bookType === "wm") {
      if (st.specialEventClub) add("WM-Lieblingsteam", NS.wmNationLabel(st.specialEventClub));
    } else {
      add("Verein zum Event", st.specialEventClub);
    }
    if (bp.clubChange) add("Vereinswechsel", "Ja");
    if (bp.customLogoUrl) add("Vereinslogo aufs Cover", "Ja");
    if (bp.bookType === "wm") add("Buchart", "WM-Buch");
    else if (bp.bookType === "saisonauftakt") add("Buchart", "Saisonauftakt-Buch");
    else if (bp.bookType === "erstleser") add("Buchart", "Erstleser-Buch");
    else if (bp.bookType === "folgebuch") add("Buchart", "Folgebuch");

    if (bp.customerHighlights) add("Geheimzutat", String(bp.customerHighlights).replace(/\n/g, " · ").replace(/•\s*/g, ""));
    if (bp.dedication && bp.dedication.text) {
      add("Persönliche Widmung", String(bp.dedication.text));
      if (bp.dedication.fromName) add("Widmung von", String(bp.dedication.fromName));
      if (bp.dedication.imageUrl) add("Widmungs-Foto", "Ja");
    }

    return lines.join("\n");
  }

  Configurator.prototype.buildCartProps = function (payload) {
    var f = this.form;
    var props = {
      _bookId: payload.bookId,
      _bookType: this.bookType,
      // Sichtbare Properties (ohne '_') werden im Warenkorb/Checkout angezeigt.
      // Key = angezeigtes Label. Reihenfolge = Anzeigereihenfolge: Name, Verein, Alter.
      "Name des Kindes": cleanText(f.child.name),
      _bookPayload: JSON.stringify(payload.bookPayload),
      // Lesbares Recap aller Angaben (versteckt; nur für die Bestellmail).
      _bookSummary: buildBookSummary(payload.bookPayload),
    };
    if (f.club && cleanText(f.club.name)) props["Verein"] = cleanText(f.club.name);
    var bsAge = NS.calcAge(f.child.birthday);
    if (bsAge != null && bsAge >= 0) props["Alter"] = bsAge + " Jahre";
    if (this.bookType === "wm" && f.story.specialEventClub) {
      props["WM-Lieblingsteam"] = NS.wmNationLabel(f.story.specialEventClub);
    }
    // Absolute Firebase-Storage-URL fuers Warenkorb-Thumbnail (kein roher base64).
    // Bevorzugt das gewatermarkte Cover (Preview + Logo); faellt auf das rohe
    // Preview zurueck, falls das Composite (noch) nicht vorliegt.
    var thumb = f.child.previewCoverImageUrl || f.child.previewImageUrl;
    if (thumb && thumb.indexOf("data:") !== 0) {
      props._previewImageUrl = thumb;
    }
    return props;
  };

  // True, wenn das Geheimzutat-Add-on im Payload aktiv ist (mind. eine Besonderheit).
  Configurator.prototype._hasAnecdoteAddon = function (payload) {
    var a = payload && payload.bookPayload && payload.bookPayload.addons;
    return Array.isArray(a) && a.indexOf("personal_anecdote") !== -1;
  };

  Configurator.prototype._hasDedicationAddon = function (payload) {
    var a = payload && payload.bookPayload && payload.bookPayload.addons;
    return Array.isArray(a) && a.indexOf("dedication") !== -1;
  };

  Configurator.prototype._hasSpecialEventAddon = function (payload) {
    var a = payload && payload.bookPayload && payload.bookPayload.addons;
    return Array.isArray(a) && a.indexOf("special_event") !== -1;
  };

  Configurator.prototype.addToCart = function (payload) {
    var props = this.buildCartProps(payload);
    var items = [{ id: Number(this.variantId), quantity: 1, properties: props }];
    // Geheimzutat als separaten Line-Item (+4,99 €) ergaenzen. _addonFor verknuepft
    // die Zeile mit dem Buch (bookId) → spaeteres Entfernen/Abgleichen im Edit-Modus.
    if (this._hasAnecdoteAddon(payload) && this.addonAnecdoteVariantId) {
      items.push({
        id: Number(this.addonAnecdoteVariantId),
        quantity: 1,
        properties: { _addonFor: payload.bookId, _addonId: "personal_anecdote" },
      });
    }
    // Persönliche Widmung als separaten Line-Item (eigenes Produkt) ergaenzen.
    if (this._hasDedicationAddon(payload) && this.dedicationVariantId) {
      items.push({
        id: Number(this.dedicationVariantId),
        quantity: 1,
        properties: { _addonFor: payload.bookId, _addonId: "dedication" },
      });
    }
    if (this._hasSpecialEventAddon(payload) && this.specialEventVariantId) {
      items.push({
        id: Number(this.specialEventVariantId),
        quantity: 1,
        properties: { _addonFor: payload.bookId, _addonId: "special_event" },
      });
    }
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: items }),
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.description || "cart/add HTTP " + res.status);
        return data;
      });
    });
  };

  // Aktualisiert eine bestehende Cart-Zeile in-place (gleicher line key) statt
  // eine neue Zeile anzulegen. /cart/change.js ersetzt die Properties komplett.
  Configurator.prototype.updateCartLine = function (payload, lineKey) {
    var self = this;
    var props = this.buildCartProps(payload);
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lineKey, quantity: 1, properties: props }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || "cart/change HTTP " + res.status);
          return data;
        });
      })
      .then(function (data) {
        // Add-on-Zeile (Geheimzutat) an den neuen Stand angleichen: im Edit-Modus
        // kann der Kunde das Add-on hinzu- oder abgewaehlt haben.
        return self._reconcileAnecdoteLine(payload).then(function () {
          return self._reconcileDedicationLine(payload).then(function () {
            return self._reconcileSpecialEventLine(payload).then(function () {
              return data;
            });
          });
        });
      });
  };

  // Gleicht die separate Geheimzutat-Zeile mit dem aktuellen Payload ab:
  // aktiv & fehlt → hinzufuegen; inaktiv & vorhanden → entfernen (quantity 0).
  // Matching ueber das _addonFor-Property (= bookId) am Add-on-Line-Item.
  Configurator.prototype._reconcileAnecdoteLine = function (payload) {
    var self = this;
    var wantAddon = this._hasAnecdoteAddon(payload);
    if (!this.addonAnecdoteVariantId) return Promise.resolve();
    var bookId = payload.bookId;
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var existing = (cart.items || []).filter(function (it) {
          var p = it.properties || {};
          return p._addonFor === bookId && it.variant_id === Number(self.addonAnecdoteVariantId);
        });
        if (wantAddon && existing.length === 0) {
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items: [
                {
                  id: Number(self.addonAnecdoteVariantId),
                  quantity: 1,
                  properties: { _addonFor: bookId, _addonId: "personal_anecdote" },
                },
              ],
            }),
          });
        }
        if (!wantAddon && existing.length > 0) {
          return fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: existing[0].key, quantity: 0 }),
          });
        }
        return null;
      });
  };

  // Gleicht die separate Widmungs-Zeile mit dem aktuellen Payload ab (wie Geheimzutat).
  Configurator.prototype._reconcileDedicationLine = function (payload) {
    var self = this;
    var wantAddon = this._hasDedicationAddon(payload);
    if (!this.dedicationVariantId) return Promise.resolve();
    var bookId = payload.bookId;
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var existing = (cart.items || []).filter(function (it) {
          var p = it.properties || {};
          return p._addonFor === bookId && it.variant_id === Number(self.dedicationVariantId);
        });
        if (wantAddon && existing.length === 0) {
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items: [
                {
                  id: Number(self.dedicationVariantId),
                  quantity: 1,
                  properties: { _addonFor: bookId, _addonId: "dedication" },
                },
              ],
            }),
          });
        }
        if (!wantAddon && existing.length > 0) {
          return fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: existing[0].key, quantity: 0 }),
          });
        }
        return null;
      });
  };

  Configurator.prototype._reconcileSpecialEventLine = function (payload) {
    var self = this;
    var wantAddon = this._hasSpecialEventAddon(payload);
    if (!this.specialEventVariantId) return Promise.resolve();
    var bookId = payload.bookId;
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var existing = (cart.items || []).filter(function (it) {
          var p = it.properties || {};
          return p._addonFor === bookId && it.variant_id === Number(self.specialEventVariantId);
        });
        if (wantAddon && existing.length === 0) {
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items: [
                {
                  id: Number(self.specialEventVariantId),
                  quantity: 1,
                  properties: { _addonFor: bookId, _addonId: "special_event" },
                },
              ],
            }),
          });
        }
        if (!wantAddon && existing.length > 0) {
          return fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: existing[0].key, quantity: 0 }),
          });
        }
        return null;
      });
  };

  // ---------------------------------------------------------------------------
  // EDIT-MODUS: Form aus bestehendem _bookPayload vorbefuellen
  // ---------------------------------------------------------------------------

  // hairType Storage→UI (ponytail→zopf), Umkehrung von normalizeHairType.
  function denormalizeHairType(v) {
    if (!v) return "";
    var lower = String(v).toLowerCase();
    return lower === "ponytail" ? "zopf" : lower;
  }

  // Befuellt this.form aus einem gespeicherten bookPayload (Umkehrung von
  // buildPayload). Startet von der leeren Form und ueberlagert vorhandene Werte.
  Configurator.prototype.hydrateForm = function (bp) {
    var form = NS.createEmptyForm();
    if (!bp || typeof bp !== "object") return form;

    var c = bp.child || {};
    var ap = c.appearance || {};
    form.child.name = c.name || "";
    form.child.lastname = c.lastname || "";
    form.child.birthday = c.birthday || "";
    form.child.gender = c.gender || "";
    form.child.kindergartenOrSchool = c.kindergartenOrSchool || "";
    form.child.hairColor = ap.hairColor || "";
    form.child.hairLength = ap.hairLength || "";
    form.child.hairType = denormalizeHairType(ap.hairType);
    form.child.faceFeatures = ap.faceFeatures || "";
    form.child.skinColor = ap.skinColor || "";
    form.child.eyeColor = ap.eyeColor || "";
    form.child.wearsGlasses = !!ap.wearsGlasses;
    form.child.previewImageUrl = ap.previewImageUrl || null;

    // city: Payload speichert ein Objekt {displayName, name, ...}. Form braucht
    // den Anzeige-String + das Detail-Objekt (cityDetails) fuer die Validierung.
    if (c.city && typeof c.city === "object") {
      form.child.cityDetails = c.city;
      form.child.city = c.city.displayName || c.city.name || "";
      if (c.city.source === "manual") {
        form.child.cityManual = true;
        form.child.cityManualText = form.child.city;
        this._cityManualOpen = true;
      }
    } else if (typeof c.city === "string") {
      form.child.city = c.city;
    }

    form.child.siblings = (c.siblings || []).map(function (s) {
      return { name: s.name || "", age: s.age != null ? String(s.age) : "", gender: s.gender || "" };
    });

    var cl = bp.club || {};
    form.club.name = cl.name || "";
    form.club.team = cl.team || "";
    form.club.position = cl.position || "";
    form.club.jerseyNumber = cl.jerseyNumber != null ? String(cl.jerseyNumber) : "";
    form.club.jerseyColor = cl.jerseyColor || "";
    form.club.teammates = (cl.teammates || []).map(function (t) {
      return { name: t.name || "", gender: t.gender || "" };
    });
    form.club.rivals = (cl.rivals || []).map(function (r) {
      return String(r || "");
    });
    var trainers = (cl.trainers || []).map(function (t) {
      return { name: t.name || "", gender: t.gender || "", parentRole: t.parentRole || null };
    });
    // Mindestens eine Trainer-Zeile fuer die List-UI (wie createEmptyForm).
    form.club.trainers = trainers.length ? trainers : [{ name: "", gender: "", parentRole: null }];

    var st = bp.story || {};
    form.story.language = st.language || "de";
    form.story.storyStyle = st.storyStyle || "classic";
    form.story.learnValues = st.learnValues || null;
    form.story.specialEvents = st.specialEvents || null;
    form.story.specialEventClub = st.specialEventClub || null;

    // Vereins-Landing-Kontext aus bestehendem Payload rekonstruieren (Edit-/
    // Folgebuch-Modus), damit fromClubLanding/clubSlug/customLogoUrl beim erneuten
    // Speichern erhalten bleiben (Parität zu Vue mapBookDocumentToForm).
    if (bp.fromClubLanding === true) {
      form._club.fromLanding = true;
      if (typeof bp.clubSlug === "string" && bp.clubSlug.trim()) {
        form._club.slug = bp.clubSlug.trim();
      }
      if (typeof bp.customLogoUrl === "string" && bp.customLogoUrl.trim()) {
        form._club.logoUrl = bp.customLogoUrl.trim();
      }
      // Ohne Geheimzutat steckt der Landing-promptDetails-Text in customerHighlights.
      // Rekonstruieren, damit der buildPayload-Fallback ihn beim Re-Save nicht verliert.
      var hadAnecdote = Array.isArray(bp.addons) && bp.addons.indexOf("personal_anecdote") !== -1;
      if (!hadAnecdote && typeof bp.customerHighlights === "string" && bp.customerHighlights.trim()) {
        form._club.promptDetails = bp.customerHighlights.trim();
      }
    }

    // Add-on Geheimzutat aus bestehendem Payload vorbefuellen (Edit-Modus).
    var pa = (bp.addonDetails && bp.addonDetails.personal_anecdote) || null;
    var paActive = Array.isArray(bp.addons) && bp.addons.indexOf("personal_anecdote") !== -1;
    if (pa || paActive) {
      form.addonDetails.personalAnecdote = {
        active: !!paActive,
        besonderheit1: (pa && pa.besonderheit1) || "",
        besonderheit2: (pa && pa.besonderheit2) || "",
        besonderheit3: (pa && pa.besonderheit3) || "",
      };
    }

    // Add-on Persönliche Widmung aus bestehendem Payload vorbefuellen (Edit-Modus).
    var dedActive = Array.isArray(bp.addons) && bp.addons.indexOf("dedication") !== -1;
    var ded = bp.dedication || null;
    if (dedActive || ded) {
      form.addonDetails.dedication = {
        active: !!dedActive,
        text: (ded && ded.text) || "",
        fromName: (ded && ded.fromName) || "",
        imageUrl: (ded && ded.imageUrl) || "",
      };
    }

    // Add-on Besonderes Event (Migration: alte Bücher mit specialEvents ohne Addon-Flag).
    var seActive = Array.isArray(bp.addons) && bp.addons.indexOf("special_event") !== -1;
    var hasLegacyEvent =
      !seActive &&
      (bp.bookType === "standard" || bp.bookType === "folgebuch") &&
      !!(
        st.specialEvents &&
        st.specialEvents !== "world_cup_men" &&
        st.specialEvents !== "season_start"
      );
    if (seActive || hasLegacyEvent || form.addonDetails.specialEvent) {
      form.addonDetails.specialEvent = { active: !!(seActive || hasLegacyEvent) };
    }

    return form;
  };

  // Verdrahtet das `bs:edit-book`-Event (vom "Buch ändern"-Button im Warenkorb).
  // So oeffnet der auf der Warenkorbseite eingebettete Konfigurator das Modal
  // direkt und vorbefuellt — ohne Sprung zur Produktseite.
  Configurator.prototype._wireEditEvents = function () {
    var self = this;
    document.addEventListener("bs:edit-book", function (e) {
      // Bereits von einer anderen Instanz behandelt? Dann nichts tun.
      if (e.defaultPrevented) return;
      var pkg = e.detail;
      if (!pkg) return;
      // Buchart muss passen (leer = Standard akzeptieren).
      if (pkg.bookType && pkg.bookType !== self.bookType) return;
      if (!self._applyEditPackage(pkg)) return;
      // Signalisiert dem Warenkorb-Handler: in-page geoeffnet, kein Seitensprung.
      e.preventDefault();
      self.render();
      self.openModal();
    });
  };

  // Liest ein vom Warenkorb hinterlegtes Edit-Paket aus sessionStorage und
  // versetzt den Konfigurator in den Edit-Modus. Fallback fuer Seiten ohne
  // eingebetteten Konfigurator (Sprung zur Produktseite via main-cart-items.js).
  Configurator.prototype._maybeEnterEditMode = function () {
    // Bereits via Event in den Edit-Modus? Dann sessionStorage ignorieren.
    if (this.editLineKey) return;

    var raw;
    try {
      raw = sessionStorage.getItem("bs_edit_book");
    } catch (e) {
      return;
    }
    if (!raw) return;

    var pkg;
    try {
      pkg = JSON.parse(raw);
    } catch (e) {
      try {
        sessionStorage.removeItem("bs_edit_book");
      } catch (e2) {}
      return;
    }

    // Nur uebernehmen, wenn dieselbe Buchart konfiguriert wird.
    if (pkg.bookType && pkg.bookType !== this.bookType) return;

    // Edit-Paket ist verbraucht — entfernen, damit ein Reload nicht erneut greift.
    try {
      sessionStorage.removeItem("bs_edit_book");
    } catch (e) {}

    if (!this._applyEditPackage(pkg)) return;

    // Konfigurator bootet vor bs-modal.js (deferred, in Reihenfolge). Das
    // Auto-Oeffnen muss daher nach dem Modal-Wiring laufen → setTimeout 0.
    var self = this;
    setTimeout(function () {
      self.openModal();
    }, 0);
  };

  // Loest einen `?folgebuch=<token>`-Parameter ein: holt das Vorgaenger-Buch-
  // Payload aus dem Firebase-Token-Tresor (Einmal-Token, von der Customer-
  // Account-Extension hinterlegt) und versetzt den Konfigurator in den
  // Folgebuch-Modus. Anders als der Edit-Modus: NEUES Buch (frische bookId,
  // neue Cart-Zeile), nur die Form wird vorbefuellt und parentBookId gesetzt.
  // Wechselt im Folgebuch-Modus die gewaehlte Buch-Variante: setzt bookType +
  // variantId (steuert Cart-Line + Preis) und baut das Schema neu (WM blendet
  // andere Felder ein/aus). Die Form-Werte bleiben erhalten (eigenes Objekt).
  // buildPayload setzt fuer bookType 'wm' automatisch specialEvents='world_cup_men',
  // daher beim Wechsel WEG von WM einen geerbten WM-Marker zuruecksetzen, damit
  // er nicht in ein Nicht-WM-Buch durchsickert.
  Configurator.prototype._applyFolgebuchVariant = function (bookType, variantId) {
    this.bookType = bookType;
    this.variantId = variantId;
    this.schema = NS.buildSchema(bookType, this._schemaOpts(bookType));
    this.steps = this.schema.steps;
    // Beim Wechsel WEG von einem Buchtyp den geerbten buchtyp-gebundenen Event-Marker
    // zurücksetzen, damit er nicht in ein anderes Buch durchsickert.
    if (bookType !== "wm" && this.form.story.specialEvents === "world_cup_men") {
      this.form.story.specialEvents = null;
    }
    if (bookType !== "saisonauftakt" && this.form.story.specialEvents === "season_start") {
      // Ausnahme: Im Folgebuch darf season_start als Event bestehen bleiben (dort explizit wählbar).
      if (!this.parentBookId) this.form.story.specialEvents = null;
    }
    if (this.stepIndex >= this.steps.length) this.stepIndex = 0;
  };

  Configurator.prototype._maybeEnterFolgebuchMode = function () {
    // Edit-Modus hat Vorrang (gleichzeitig ergibt keinen Sinn).
    if (this.editLineKey) return;

    var token = null;
    try {
      token = new URLSearchParams(window.location.search).get("folgebuch");
    } catch (e) {
      return;
    }
    if (!token) return;
    if (!this.fnBase) return;

    // Token aus der URL entfernen, damit ein Reload den (verbrauchten) Token
    // nicht erneut einzuloesen versucht.
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete("folgebuch");
      window.history.replaceState({}, document.title, url.toString());
    } catch (e) {}

    // Sofort Feedback geben: der Redeem-Fetch + Modal-Aufbau dauert spuerbar,
    // daher ein Vollbild-Lade-Overlay ab Token-Erkennung bis das Modal offen ist.
    this._showFolgebuchLoader();

    var self = this;
    fetch(this.fnBase + "/folgebuchHandoffRedeem", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: token }),
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        if (!data || !data.bookPayload) {
          self._hideFolgebuchLoader();
          return;
        }
        self.form = self.hydrateForm(data.bookPayload);
        self._kosManual = !!self.form.child.kindergartenOrSchool;
        self._formHydrated = true;
        self.parentBookId = data.parentBookId || null;
        self.folgebuchTitle = data.parentTitle || "";
        // Anders als frueher (stiller Abbruch bei abweichender Buchart): im
        // Folgebuch darf die Variante gewaehlt werden. Vorauswahl = Buchart des
        // Vorgaengerbuchs, falls in der gepflegten Liste; sonst Seiten-Default.
        var initial = null;
        for (var i = 0; i < self.folgebuchVariants.length; i++) {
          if (self.folgebuchVariants[i].book_type === data.bookType) {
            initial = self.folgebuchVariants[i];
            break;
          }
        }
        if (initial) self._applyFolgebuchVariant(initial.book_type, initial.variant_id);
        else {
          // Keine passende Variante gepflegt → Schema trotzdem im Folgebuch-Modus neu
          // bauen, damit "Saisonauftakt" als Event wählbar wird (isFolgebuch=true).
          self.schema = NS.buildSchema(self.bookType, self._schemaOpts(self.bookType));
          self.steps = self.schema.steps;
        }
        // Frische bookId beibehalten (NEUES Buch), kein Edit-Modus.
        self.editLineKey = null;
        self.stepIndex = 0;
        self.stepError = "";
        self.render();
        self.openModal();
        self._hideFolgebuchLoader();
      })
      .catch(function () {
        // Stiller Fehlschlag: Konfigurator bleibt im Normalmodus (leere Form).
        self._hideFolgebuchLoader();
      });
  };

  // Vollbild-Lade-Overlay fuer den Folgebuch-Handoff (Token-Redeem + Modal-Aufbau).
  Configurator.prototype._showFolgebuchLoader = function () {
    if (this._fbLoader) return;
    var ov = document.createElement("div");
    ov.className = "bs-folgebuch-loading";
    ov.setAttribute("role", "status");
    ov.setAttribute("aria-live", "polite");
    var box = document.createElement("div");
    box.className = "bs-folgebuch-loading__box";
    var sp = document.createElement("span");
    sp.className = "bs-c__spinner";
    sp.setAttribute("aria-hidden", "true");
    var tx = document.createElement("span");
    tx.className = "bs-folgebuch-loading__text";
    tx.textContent = "Folgebuch wird vorbereitet …";
    box.appendChild(sp);
    box.appendChild(tx);
    ov.appendChild(box);
    document.body.appendChild(ov);
    this._fbLoader = ov;
  };

  Configurator.prototype._hideFolgebuchLoader = function () {
    if (this._fbLoader && this._fbLoader.parentNode) {
      this._fbLoader.parentNode.removeChild(this._fbLoader);
    }
    this._fbLoader = null;
  };

  // Wertet `?club=<handle>` aus (CTA der Vereins-Metaobject-Seite): schlaegt den
  // Verein in window.BS_CONFIGURATOR_CLUBS nach, befuellt Verein/Trikot/Stadt vor
  // und merkt sich den Landing-Kontext (form._club) fuer den Payload. Faithful zu
  // Vue Create.vue applyClubConfigFromLanding (Slug case-insensitiv, raw jerseyColor,
  // Camp blendet Events + Geheimzutat aus, Stadt via Open-Meteo-ID mit Name-Fallback).
  Configurator.prototype._maybeApplyClub = function () {
    // Edit-/Folgebuch-Modus hat Vorrang (kein Club-Prefill ueberschreiben).
    if (this.editLineKey || this.parentBookId) return;

    var raw = null;
    try {
      raw = new URLSearchParams(window.location.search).get("club");
    } catch (e) {
      return;
    }
    if (!raw) return;

    var registry = window.BS_CONFIGURATOR_CLUBS || {};
    var key = String(raw).trim().toLowerCase();
    var club = registry[key];
    if (!club) return;

    // Param aus der URL entfernen (sauberer Reload, kein erneutes Anwenden).
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete("club");
      window.history.replaceState({}, document.title, url.toString());
    } catch (e) {}

    var f = this.form;
    var skipClubPrefill = club.skipClubPrefill === true;
    var skipCityPrefill = club.skipCityPrefill === true;

    if (skipClubPrefill) {
      f.club.name = "";
      f.club.jerseyColor = "";
    } else {
      f.club.name = (club.name || "").trim();
      // Wie Vue: rohen jerseyColor-Wert uebernehmen (kein Mapping), damit das
      // Firestore-Dokument 1:1 zu Vue-erzeugten Vereins-Buechern passt.
      if (club.jerseyColor && typeof club.jerseyColor === "string" && club.jerseyColor.trim()) {
        f.club.jerseyColor = club.jerseyColor.trim();
      }
    }

    f._club = {
      fromLanding: true,
      slug: (club.slug && String(club.slug).trim()) || String(raw).trim(),
      name: club.name && typeof club.name === "string" ? club.name.trim() : null,
      logoUrl: club.logoUrl && typeof club.logoUrl === "string" ? club.logoUrl.trim() : null,
      promptDetails:
        club.promptDetails && typeof club.promptDetails === "string" && club.promptDetails.trim()
          ? club.promptDetails.trim()
          : null,
      hideSpecialEvents: club.hideSpecialEvents === true,
      hidePersonalAnecdote: club.hidePersonalAnecdote === true,
    };

    if (club.hideSpecialEvents === true) {
      f.story.specialEvents = null;
      f.story.specialEventClub = null;
      if (f.addonDetails && f.addonDetails.specialEvent) {
        f.addonDetails.specialEvent.active = false;
      }
    }

    if (club.hidePersonalAnecdote === true && f.addonDetails && f.addonDetails.personalAnecdote) {
      f.addonDetails.personalAnecdote.active = false;
    }

    // Stadt zuruecksetzen, dann (falls erlaubt) vorbefuellen.
    f.child.city = "";
    f.child.cityDetails = null;
    f.child.cityManual = false;
    f.child.cityManualText = "";
    this._cityManualOpen = false;
    this._cityManualReported = false;
    this._cityManualReportedText = "";

    if (!skipCityPrefill) {
      var cityName = club.city && typeof club.city === "string" ? club.city.trim() : "";
      var cityId =
        club.cityId != null && isFinite(Number(club.cityId)) ? Number(club.cityId) : 0;
      // Sofortiger Name-Fallback, damit auch ohne/while Lookup etwas drin steht.
      if (cityName) f.child.city = cityName;

      // Volle City-Details via Open-Meteo-ID (wie Vue), nur echte GeoNames-IDs > 0.
      if (cityId > 0) {
        var self = this;
        fetch("https://geocoding-api.open-meteo.com/v1/get?id=" + cityId)
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .then(function (data) {
            var cd =
              data && (data.id || data.name)
                ? data
                : data && data.results && data.results[0]
                ? data.results[0]
                : null;
            if (!cd) return;
            var label = [cd.name, cd.admin1, cd.country_code].filter(Boolean).join(", ");
            self.form.child.cityDetails = {
              displayName: label,
              id: cd.id || cd.name,
              name: cd.name || "",
              admin1: cd.admin1 || "",
              admin1_id: cd.admin1_id != null ? cd.admin1_id : null,
              admin2: cd.admin2 || null,
              admin2_id: cd.admin2_id != null ? cd.admin2_id : null,
              admin3: cd.admin3 || null,
              admin3_id: cd.admin3_id != null ? cd.admin3_id : null,
              admin4: cd.admin4 || null,
              admin4_id: cd.admin4_id != null ? cd.admin4_id : null,
              country: cd.country || "",
              country_code: cd.country_code || "",
              country_id: cd.country_id != null ? cd.country_id : null,
              latitude: cd.latitude != null ? cd.latitude : null,
              longitude: cd.longitude != null ? cd.longitude : null,
              elevation: cd.elevation != null ? cd.elevation : null,
              timezone: cd.timezone || "",
              population: cd.population != null ? cd.population : null,
              feature_code: cd.feature_code || "",
            };
            self.form.child.city = label;
            self.render();
          })
          .catch(function () {});
      }
    }

    // Direkt-Einstieg: Besucher kommen via Club-CTA (?club=) auf die PDP und
    // erwarten — wie im Vue-Original — dass der Konfigurator sofort aufgeht,
    // ohne Extra-Klick auf "Jetzt personalisieren". Im Modal-Modus oeffnen wir
    // daher den Dialog automatisch. Verzoegert (setTimeout 0), damit bs-modal.js
    // den Trigger zuvor verdrahtet hat (defer-Reihenfolge: configurator < modal,
    // und _maybeApplyClub entfernt den ?club=-Param bereits per replaceState,
    // sodass bs-modal ihn selbst nicht mehr sehen koennte).
    var root = this.root;
    window.setTimeout(function () {
      var app = root.closest("[data-bs-app]");
      if (!app || app.getAttribute("data-display-mode") !== "modal") return;
      var dialog = app.querySelector("[data-bs-modal]");
      if (!dialog || dialog.open) return; // kein Dialog oder schon offen → fertig

      // Bevorzugt ueber den Trigger oeffnen, damit bs-modal.js seinen Scroll-Lock
      // etc. setzt. ABER: die drei Extension-Scripts sind separate CDN-Requests;
      // `defer` garantiert nur die Ausfuehrungs-REIHENFOLGE, nicht das Timing.
      // Solange bs-modal.js noch laedt, dreht sich der Event-Loop und dieser
      // setTimeout feuert evtl. BEVOR der Trigger verdrahtet ist → Klick ins Leere.
      var trigger = app.querySelector("[data-bs-open]");
      if (trigger) trigger.click();
      if (dialog.open) return; // bs-modal war schon verdrahtet → fertig

      // Fallback (bs-modal noch nicht ausgefuehrt): Dialog direkt oeffnen und den
      // Body-Scroll-Lock selbst setzen (gleiche Klasse wie bs-modal). bs-modal
      // verdrahtet Close/Backdrop/ESC nachtraeglich am selben Dialog — kompatibel.
      if (typeof dialog.showModal === "function") {
        try {
          dialog.showModal();
        } catch (e) {
          dialog.setAttribute("open", "");
        }
      } else {
        dialog.setAttribute("open", "");
      }
      document.documentElement.classList.add("bs-modal-open");
    }, 0);
  };

  // Uebernimmt ein Edit-Paket in den Form-State (Form vorbefuellen, editLineKey
  // setzen, bookId beibehalten). Gibt true zurueck, wenn das Paket gueltig war.
  Configurator.prototype._applyEditPackage = function (pkg) {
    if (!pkg || !pkg.lineKey || !pkg.bookPayload) return false;
    this.editLineKey = pkg.lineKey;
    this._formHydrated = true;
    // bookId beibehalten, damit das Buch identisch bleibt.
    if (pkg.bookId) this.bookId = pkg.bookId;
    this.form = this.hydrateForm(pkg.bookPayload);
    this._kosManual = !!this.form.child.kindergartenOrSchool;
    this.stepIndex = 0;
    this.stepError = "";
    return true;
  };

  // Setzt den Konfigurator fuer ein weiteres Buch zurueck (neue IDs, leere Form),
  // ohne den Edit-Modus zu aktivieren.
  Configurator.prototype._resetForNewBook = function () {
    this.form = NS.createEmptyForm();
    this._kosManual = false;
    this._formHydrated = false;
    NS.applyAddonExpandDefaults(this.form, this._addonExpandCtx());
    this.bookId = this._newBookId();
    this.previewSessionId = this._newBookId();
    this.stepIndex = 0;
    this.stepError = "";
    this.editLineKey = null;
    this.parentBookId = null;
    this.previewLoading = false;
    this.previewError = "";
    this.previewFromCache = null;
    this.previewAutoTriggered = false;
    this.previewGeneratedSignature = null;
    this._previewHintOpen = false;
    this._previewFeedbackOpen = false;
    this._feedbackOpen = "";
    this._feedbackSent = {};
    this._feedbackDraft = { email: "", message: "" };
    this._feedbackBusy = false;
    this._feedbackError = "";
    this._cityManualOpen = false;
    this._cityManualReported = false;
    this._cityManualReportedText = "";
  };

  // ---------------------------------------------------------------------------
  // BOOT
  // ---------------------------------------------------------------------------
  function boot() {
    document.querySelectorAll("[data-bs-configurator]").forEach(function (root) {
      if (root.__bsBooted) return;
      root.__bsBooted = true;
      try {
        new Configurator(root);
      } catch (e) {
        console.error("[bs-configurator]", e);
        var m = root.querySelector("[data-bs-mount]");
        if (m) m.innerHTML = '<p class="bs-c__error">Konfigurator konnte nicht geladen werden.</p>';
      }
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
